// =============================================================================
// TRANSLATIONS — 7 Languages
// =============================================================================
// FR (default), EN, DE, ES, PT-BR, JA, NL
// =============================================================================

export type Language = 'fr' | 'en' | 'de' | 'es' | 'pt-BR' | 'ja' | 'nl';

export const localeMap: Record<Language, string> = {
  fr: 'fr-FR',
  en: 'en-US',
  de: 'de-DE',
  es: 'es-ES',
  'pt-BR': 'pt-BR',
  ja: 'ja-JP',
  nl: 'nl-NL',
};

export const translations = {
  // ===========================================================================
  // FRANCAIS (default)
  // ===========================================================================
  fr: {
    nav: {
      dashboard: 'Tableau de bord',
      googleAds: 'Google Ads',
      meta: 'Meta CAPI',
      billing: 'Facturation',
      settings: 'Paramètres',
    },
    common: {
      save: 'Enregistrer',
      cancel: 'Annuler',
      connect: 'Connecter',
      disconnect: 'Déconnecter',
      enable: 'Activer',
      disable: 'Désactiver',
      test: 'Tester',
      refresh: 'Actualiser',
      loading: 'Chargement...',
      error: 'Erreur',
      success: 'Succès',
      warning: 'Attention',
      connected: 'Connecté',
      notConnected: 'Non connecté',
      active: 'Actif',
      disabled: 'Désactivé',
      pending: 'En attente',
      sent: 'Envoyé',
      thisMonth: 'Ce mois-ci',
      selectAccount: 'Sélectionner le compte',
      selectAction: 'Sélectionner l\'action',
      createNew: 'Créer nouveau',
      // Generic fallback labels used across channel pages. Prefer channel-
      // specific keys when they exist; these are only for the bottom-of-the-
      // barrel "something went wrong" case when the backend error has no
      // message.
      actionFailed: 'Une erreur est survenue. Veuillez réessayer.',
      configFailed: 'La configuration a échoué. Veuillez réessayer.',
      gateNeedsPlan: 'Cette fonctionnalité nécessite le plan {plan}',
      gateNeedsSubscription: 'Activez votre abonnement pour débloquer cette fonctionnalité',
      gateBody: 'Votre capture de données continue normalement. Passez au plan {plan} pour activer cette fonctionnalité en quelques clics.',
      gateCta: 'Voir les plans',
      disableChannelTitle: 'Désactiver l\'envoi vers {channel} ?',
      disableChannelBody: 'La capture continue à 100% : chaque conversion reste enregistrée dans Trackful. Seul l\'envoi vers {channel} est mis en pause.',
      disableChannelSafety: 'À la réactivation, toutes les conversions capturées pendant la pause seront automatiquement renvoyées à {channel}. Un email de confirmation vous sera envoyé.',
    },
    dashboard: {
      title: 'Tableau de bord',
      totalConversions: 'Conversions totales',
      totalRevenue: 'Revenu total',
      googleAdsTracked: 'Google Ads suivies',
      deliveryRate: 'Livrées aux régies',
      metaTracked: 'Meta suivies',
      ofConversions: 'des conversions',
      attributionRate: 'Taux d\'attribution',
      conversionsWithAttribution: 'Conversions avec attribution click ID',
      excellent: 'Excellent',
      good: 'Bien',
      needsImprovement: 'À améliorer',
      integrationStatus: 'Statut des intégrations',
      eventMatchQuality: 'Qualité de correspondance Meta',
      emqNotAvailable: 'Données EMQ non disponibles. Connectez Meta CAPI et envoyez des événements.',
      recentConversions: 'Conversions récentes',
      deliveryPerChannel: 'Livraison par canal : chaque conversion est envoyée à tous les canaux actifs; chaque plateforme fait ensuite sa propre attribution.',
      noConversions: 'Aucune conversion',
      noConversionsDesc: 'Les conversions apparaîtront ici une fois que vous recevrez des commandes avec le suivi activé.',
      order: 'Commande',
      revenue: 'Revenu',
      googleAds: 'Google Ads',
      meta: 'Meta',
      attribution: 'Attribution',
      date: 'Date',
      warningNotConnected: 'Ni Google Ads ni Meta CAPI ne sont connectés. Configurez vos intégrations pour commencer le suivi.',
      warningGoogleNotConnected: 'Google Ads n\'est pas connecté. Configurez-le pour suivre les conversions.',
      warningMetaNotConnected: 'Meta CAPI n\'est pas connecté. Configurez-le pour suivre les conversions.',
      integrationLoadError: 'Impossible de charger le statut des intégrations. Veuillez actualiser la page.',
      failedThisMonth: 'Échecs ce mois',
      yes: 'Oui',
      no: 'Non',
      direct: 'Direct',
    },
    setup: {
      activeTitle: 'Votre suivi server-side entreprise est actif',
      activeBadge: 'Actif',
      activeBody: 'Rien d\'autre à faire. Nous nous occupons du reste : capture des événements, hachage des données personnelles, envoi aux régies et auto-réparation en cas d\'erreur.',
      activeLiveChannels: 'Canaux actifs',
      inactiveTitle: 'Activez votre suivi server-side',
      inactiveBody: 'Connectez au moins une régie publicitaire. Un seul canal suffit pour être en ligne : Trackful configure automatiquement toute la mise en place en coulisses (action de conversion, pixel, attribution).',
      connect: 'Connecter',
      connected: 'Connecté',
      needsAction: 'Terminer',
      connectFailed: 'La connexion a échoué. Veuillez réessayer.',
      pixelLive: 'Pixel actif, événements capturés',
      pixelWaiting: 'En attente des premiers événements du pixel',
      clickTrackerTitle: 'Activez le suivi des clics sur votre thème',
      clickTrackerActive: 'Actif',
      clickTrackerCta: 'Activer maintenant',
      clickTrackerBody: "Le tracker de clics capture les identifiants publicitaires (gclid, fbclid...) et pose des cookies serveur valables 1 an : c'est le prérequis de l'attribution précise. Shopify ne permet pas de l'activer automatiquement : un clic sur Activer, puis Enregistrer dans l'éditeur de thème.",
      clickTrackerHint: "Après l'enregistrement, ouvrez votre boutique une fois : cette étape passera au vert automatiquement.",
      sendTestEvent: 'Envoyer une conversion de test',
      testEventOk: 'Conversion de test acceptée par Google ✓',
      testEventFail: 'Le test a échoué : ouvrez la page Google Ads pour le détail.',
      consentNotice: 'Conformité : sans bannière de cookies (Consent Mode v2), les conversions UE ne peuvent pas transporter de données client vers Meta et GA4. Configurez votre CMP pour une couverture maximale.',
      advanced: 'Options avancées',
      funnelTitle: 'Transmission de l\'entonnoir complet',
      funnelBody: 'Envoie tout le parcours d\'achat (produits vus, ajouts au panier, checkouts) à vos régies comme signaux d\'optimisation supplémentaires, pas seulement les achats. Les achats sont toujours transmis.',
      funnelOn: 'Activée',
      funnelOff: 'Désactivée',
      funnelChannelsTitle: 'Ce que chaque régie reçoit',
      funnelChannelsBody: 'Les achats partent toujours. Le reste du parcours (produits vus, ajouts au panier, checkouts) ne part que lorsque nous pouvons garantir qu\'il ne sera pas compté deux fois.',
      funnelSends: 'Parcours complet',
      funnelPurchasesOnly: 'Achats seulement',
      funnelWhyOwn: 'Nos propres actions de conversion : rien de ce qui existe chez vous ne peut être compté deux fois.',
      funnelWhyStamped: 'Votre balise ({sources}) porte maintenant notre identifiant d\'événement : la régie n\'en compte qu\'un.',
      funnelWhyEmpty: 'La propriété ne recevait rien avant nous.',
      funnelWhyDeclared: 'Vous nous avez dit que Trackful est votre seule source ici.',
      funnelWhyCollision: 'Une autre source alimente déjà cette régie ({sources}). Nous n\'envoyons que les achats pour ne pas doubler vos chiffres.',
      funnelAsk: 'Une autre balise envoie-t-elle déjà des événements à cette régie depuis le navigateur ? Les canaux de vente natifs de Shopify comptent.',
      funnelAskKeep: 'Oui, je la garde',
      funnelAskOnly: 'Non, Trackful est ma seule source',
      funnelAskWhy: 'Nous ne pouvons pas le voir nous-mêmes : les pixels des canaux natifs Shopify tournent dans un bac à sable isolé.',
      pausedTitle: 'Envoi aux régies en pause (données conservées)',
      pausedCta: 'Mettre à niveau',
      pausedBody: 'Vous avez atteint la limite de votre plan. Nous continuons de tout capturer, mais l\'envoi vers vos régies est en pause. Mettez à niveau pour reprendre : nous renverrons automatiquement tout ce qui a été capturé.',
      pausedBodyCount: 'Limite de plan atteinte. {count} conversion(s) capturée(s) en attente d\'envoi. Nous continuons de tout capturer ; mettez à niveau pour reprendre l\'envoi et tout renvoyer automatiquement.',
    },
    googleAds: {
      title: 'Google Ads',
      subtitle: 'Connectez Google Ads pour le suivi Enhanced Conversions',
      connectionStatus: 'Statut de connexion',
      account: 'Compte',
      conversionAction: 'Action de conversion',
      lastSync: 'Dernière synchronisation',
      testConnection: 'Tester la connexion',
      connectGoogleAds: 'Connecter Google Ads',
      connectDescription: 'Connectez votre compte Google Ads pour commencer le suivi des conversions avec Enhanced Conversions.',
      enhancedConversions: 'Enhanced Conversions',
      enhancedConversionsDesc: 'Enhanced Conversions améliore la précision du suivi en envoyant de manière sécurisée les données client hashées (email, téléphone, adresse) à Google Ads.',
      enableEnhancedConversions: 'Activer Enhanced Conversions',
      enhancedConversionsHelp: 'Nécessite le consentement client pour ad_user_data selon RGPD/Consent Mode v2',
      ecActionRequiredTitle: 'Action requise dans Google Ads',
      ecActionRequiredIntro: 'Enhanced Conversions ne peut pas être activé depuis cette application. Vous devez le configurer dans Google Ads (Objectifs > Conversions > Paramètres) :',
      ecStep1: '1. Activez « Enhanced conversions for web »',
      ecStep2: '2. Acceptez les conditions relatives aux données client',
      ecStep3: '3. Choisissez « API Google Ads » comme méthode',
      ecNote: 'Tant que ces étapes ne sont pas effectuées, les données client hachées envoyées ne sont pas traitées par Google.',
      ecReady: 'Enhanced Conversions est prêt : les conditions relatives aux données client sont acceptées sur ce compte Google Ads.',
      conversionActionsTitle: 'Actions de conversion',
      conversionActionsDesc: "Trackful transmet l'achat (action principale) et peut aussi transmettre les étapes du parcours comme actions secondaires.",
      autoCreatedNote: 'Créées automatiquement dans votre compte Google Ads au moment de la sélection du compte. Retrouvez-les dans Objectifs > Conversions.',
      funnelOffHint: "La transmission de l'entonnoir complet est désactivée (Tableau de bord > Options avancées) : ces interrupteurs s'appliqueront aux événements secondaires une fois qu'elle sera activée. Les achats sont toujours transmis.",
      disableTitle: 'Désactiver l\'envoi vers Google Ads ?',
      disableConfirmBody: 'La capture continue à 100% : chaque conversion reste enregistrée dans Trackful. Seul l\'envoi vers Google Ads est mis en pause.',
      disableConfirmSafety: 'À la réactivation, toutes les conversions capturées pendant la pause seront automatiquement renvoyées à Google Ads. Un email de confirmation vous sera envoyé.',
      funnelPurchase: 'Achat',
      funnelPurchaseDesc: "L'événement le plus important : la vente. Toujours transmis.",
      primaryBadge: 'Principal',
      createPrimaryAction: "Créer l'action d'achat",
      activateAction: 'Activer',
      funnelProductView: 'Vue produit',
      funnelProductViewDesc: 'Quand un visiteur consulte une fiche produit.',
      funnelAddToCart: 'Ajout au panier',
      funnelAddToCartDesc: "Quand un produit est ajouté au panier. Fort signal d'intention.",
      funnelBeginCheckout: 'Début de paiement',
      funnelBeginCheckoutDesc: 'Quand un visiteur démarre le paiement.',
      funnelAddPaymentInfo: 'Ajout des infos de paiement',
      funnelAddPaymentInfoDesc: 'Quand les informations de paiement sont saisies.',
      setupInstructions: 'Instructions de configuration',
      step1: '1. Cliquez sur "Connecter Google Ads" pour autoriser l\'accès',
      step2: '2. Sélectionnez le compte Google Ads à utiliser',
      step3: '3. Choisissez ou créez une action de conversion pour les achats',
      step4: '4. Activez Enhanced Conversions pour une meilleure précision',
      selectAccountTitle: 'Sélectionner un compte Google Ads',
      availableAccounts: 'Comptes disponibles',
      noAccounts: 'Aucun compte trouvé. Assurez-vous que votre compte Google a accès à au moins un compte Google Ads.',
      selectAccountPrompt: 'Aucun compte sélectionné. Choisissez le compte Google Ads à suivre.',
      selectConversionTitle: 'Sélectionner une action de conversion',
      selectConversionDesc: 'Sélectionnez une action de conversion existante ou créez-en une nouvelle pour suivre les achats.',
      availableActions: 'Actions de conversion disponibles',
      noActions: 'Aucune action de conversion trouvée. Cliquez sur "Créer nouveau" pour en créer une.',
      tagBasedAction: "Action basée sur un tag : ne peut pas recevoir d'envois côté serveur. Choisissez une action Trackful ou créez-en une.",
      changeAction: 'Changer l\'action',
      conflictTitle: 'Risque de double comptage détecté',
      conflictDesc: "Ces autres actions de conversion Achat sont aussi définies comme Principales dans votre compte Google Ads. Tant qu'il y a plusieurs actions Achat principales, vos achats sont comptés en double dans la colonne Conversions et dans les enchères :",
      conflictFix: 'Faire de Trackful la seule action principale',
      conflictKeep: 'Conserver tel quel',
      conflictConfirmDesc: 'Les actions ci-dessous passeront en Secondaire (observation) dans votre compte Google Ads. Elles resteront suivies mais ne compteront plus dans la colonne Conversions ni dans les enchères. Réversible à tout moment.',
      conflictFixed: 'Terminé. Les actions suivantes sont passées en observation :',
      conflictUndo: 'Annuler',
      disconnectTitle: 'Déconnecter Google Ads',
      disconnectConfirm: 'Êtes-vous sûr de vouloir déconnecter Google Ads ? Le suivi des conversions sera arrêté jusqu\'à la reconnexion.',
      disconnectRemoveActions: 'Supprimer aussi les actions de conversion créées par Trackful dans Google Ads',
      disconnectRemoveActionsHelp: "Elles passeront au statut « Supprimée » : elles cessent d'enregistrer, mais vos données historiques restent dans vos rapports.",
      testSuccess: 'Test réussi ! La configuration est valide.',
      testRequestId: 'ID de requête Google',
      testFailed: 'Le test a échoué',
      enableTracking: 'Activer le suivi',
      disableTracking: 'Désactiver le suivi',
      actionFailed: 'Erreur. Veuillez réessayer.',
    },
    meta: {
      title: 'Meta CAPI',
      subtitle: 'Configurez Meta Conversions API pour le suivi côté serveur',
      connectionStatus: 'Statut de connexion',
      pixelId: 'ID Pixel',
      testEventCode: 'Code événement test',
      lastSync: 'Dernière synchronisation',
      sendTestEvent: 'Envoyer événement test',
      updateConfiguration: 'Modifier la configuration',
      configureMeta: 'Configurer Meta CAPI',
      connectDescription: 'Connectez votre Meta Pixel pour commencer le suivi des conversions via l\'API Conversions.',
      emq: 'Qualité de correspondance (EMQ)',
      emqDesc: 'L\'EMQ mesure la qualité de correspondance entre vos données d\'événements et les utilisateurs Meta. Des scores plus élevés signifient une meilleure attribution et performance publicitaire.',
      overallScore: 'Score global',
      recommendations: 'Recommandations',
      emqNotAvailable: 'Données EMQ non disponibles. Envoyez des événements et actualisez pour voir les scores.',
      setupInstructions: 'Instructions de configuration',
      step1: '1. Accédez à votre Gestionnaire d\'événements Meta',
      step2: '2. Trouvez votre ID Pixel (Paramètres > ID Pixel)',
      step3: '3. Générez un Token d\'accès (Paramètres > API Conversions > Générer Token)',
      step4: '4. Optionnellement, créez un Code d\'événement test pour le débogage',
      step5: '5. Entrez ces valeurs dans le formulaire de configuration',
      configureTitle: 'Configurer Meta CAPI',
      saveConfiguration: 'Enregistrer la configuration',
      pixelIdPlaceholder: '1234567890123456',
      pixelIdHelp: 'Votre ID Meta Pixel depuis le Gestionnaire d\'événements',
      accessToken: 'Token d\'accès',
      accessTokenPlaceholder: 'EAAx...',
      accessTokenHelp: 'Token d\'accès API Conversions (gardez-le secret)',
      testEventCodeOptional: 'Code événement test (Optionnel)',
      testEventCodePlaceholder: 'TEST12345',
      testEventCodeHelp: 'Utilisez ceci pour envoyer des événements vers Test Events dans le Gestionnaire d\'événements',
      disconnectTitle: 'Déconnecter Meta CAPI',
      disconnectConfirm: 'Êtes-vous sûr de vouloir déconnecter Meta CAPI ? Le suivi des conversions sera arrêté jusqu\'à la reconnexion.',
      testSuccess: 'Événement test envoyé avec succès ! Événements reçus :',
      testFailed: 'Le test a échoué',
      required: 'L\'ID Pixel et le Token d\'accès sont requis',
      enableTracking: 'Activer le suivi',
      disableTracking: 'Désactiver le suivi',
      actionFailed: 'Erreur. Veuillez réessayer.',
    },
    billing: {
      title: 'Facturation',
      subtitle: 'Gérez votre abonnement et suivez votre utilisation',
      currentPlan: 'Plan actuel',
      usage: 'Utilisation',
      ordersUsed: 'Commandes suivies',
      ordersRemaining: 'Événements restants',
      unlimited: 'Illimité',
      ofLimit: 'de la limite',
      trial: 'Essai gratuit',
      trialActive: 'Essai actif',
      trialEndsIn: 'L\'essai se termine dans',
      trialDaysRemaining: 'restants',
      days: 'jours',
      upgradeNow: 'Mettre à niveau',
      upgradePlan: 'Mettre à niveau le plan',
      downgradePlan: 'Rétrograder le plan',
      cancelSubscription: 'Annuler l\'abonnement',
      scheduledChange: 'Passage à {plan} le {date}. D\'ici là vous conservez {current}.',
      alreadyCancelled: 'Annulé. L\'accès court jusqu\'à la fin de la période.',
      // Billing cadence. `savePercent` and `billedYearly` are templates:
      // the numbers come from the API so the page can never quote a
      // discount the charge does not match.
      billMonthly: 'Mensuel',
      billAnnually: 'Annuel',
      savePercent: '{percent} % d’économie',
      billedYearly: 'facturé {amount} par an',
      switchToYearly: 'Passer à l’annuel',
      switchToMonthly: 'Passer au mensuel',
      selectPlan: 'Sélectionner ce plan',
      currentPlanBadge: 'Plan actuel',
      month: '/mois',
      ordersPerMonth: 'commandes suivies/mois',
      subscribe: 'S\'abonner',
      havePartnerCode: 'Vous avez un code partenaire ?',
      partnerCodeApplied: 'Code partenaire appliqué',
      partnerCodeAppliedHelp: 'Votre remise est appliquée avant votre confirmation : le prix que Shopify vous affiche la contient déjà.',
      partnerCodePrompt: 'Si un partenaire vous a donné un code, saisissez-le avant de vous abonner.',
      partnerCodeFirstOnly: 'Un code partenaire se saisit avant votre premier abonnement. Une remise en cours vous suit lors d\'un changement de plan, tant que vous restez sur la même périodicité.',
      partnerCodePlaceholder: 'Facultatif',
      partnerCodeLabel: 'Code partenaire',
      partnerCodeHelp: 'Appliqué à votre premier mois. Un code inconnu signifie simplement aucune remise.',
      sectionChannels: 'Canaux publicitaires',
      sectionTracking: 'Tracking & attribution',
      sectionCompliance: 'Conformité & consentement',
      sectionInsights: 'Données & pilotage',
      sectionSupport: 'Support',
      features: 'Fonctionnalités',
      plans: 'Plans disponibles',
      starter: 'Starter',
      growth: 'Growth',
      pro: 'Pro',
      warningNearLimit: 'Vous approchez de votre limite d\'événements mensuelle.',
      warningOverLimit: 'Vous avez dépassé votre limite d\'événements. Mettez à niveau pour continuer le suivi.',
      warningTrialEnding: 'Votre essai se termine bientôt. Abonnez-vous pour continuer.',
      billingHistory: 'Historique de facturation',
      noHistory: 'Aucun historique de facturation',
      recommendation: 'Recommandation',
      upgradeToRecommended: 'Mettre à niveau vers {plan}',
      plansLoadError: 'Impossible de charger les plans. Veuillez actualiser la page.',
      actionFailed: 'Erreur lors du changement de plan. Veuillez réessayer.',
      subscriptionSuccess: 'Abonnement activé avec succès ! Votre suivi est maintenant actif.',
      planNowSuccess: 'C\'est fait. Vous êtes maintenant sur le plan {plan}.',
      scheduledSuccess: 'C\'est confirmé. Vous passez à {plan} à la fin de la période déjà réglée.',
      scheduledBadge: 'Programmé',
      cancelConfirmationTrial: 'Vous êtes encore en essai gratuit, rien ne vous a été facturé. Annuler vous remet immédiatement sur le plan Free.',
      activationFailed: 'Nous n\'avons pas pu confirmer votre abonnement. Réessayez ou contactez le support.',
      planName: 'Plan',
      priceLabel: 'Prix',
      upgrade: 'Mettre à niveau',
      downgrade: 'Rétrograder',
      historyDate: 'Date',
      historyType: 'Type',
      historyAmount: 'Montant',
      historyStatus: 'Statut',
      cancelTitle: 'Annuler l\'abonnement',
      confirmCancel: 'Oui, annuler',
      keepPlan: 'Garder mon plan',
      cancelConfirmation: 'Êtes-vous sûr de vouloir annuler ? Votre plan restera actif jusqu\'à la fin de la période de facturation en cours.',
      featureLabels: {
        google_ads: 'Google Ads Enhanced Conversions',
        meta_capi: 'API Conversions Meta',
        identity_resolution: 'Résolution d\'identité',
        basic_support: 'Support basique',
        priority_support: 'Support prioritaire',
        dedicated_support: 'Support dédié',
        advanced_analytics: 'Analytics avancés',
        api_access: 'Accès API',
        ga4: 'Google Analytics 4 (Measurement Protocol)',
        tiktok: 'TikTok Events API',
        pinterest: 'Pinterest Conversions API',
        snapchat: 'Snapchat Conversions API',
        reconciliation: 'Réconciliation des conversions',
        live_debugger: 'Débogueur d\'événements en temps réel',
        alerts: 'Alertes de tracking',
        export_csv: 'Export CSV',
        webhooks_out: 'Webhooks sortants',
        server_side_tracking: 'Tracking server-side (contourne les bloqueurs de pub)',
        one_year_cookies: 'Cookies serveur 1 an (contournement Safari ITP)',
        click_id_capture: 'Capture des click IDs (gclid, fbclid, ttclid, etc.)',
        full_funnel: 'Transmission de l\'entonnoir complet (vues, paniers, checkouts)',
        dedup: 'Déduplication déterministe des événements',
        auto_backfill: 'Renvoi automatique après une pause (quota, canal désactivé)',
        consent_mode: 'Google Consent Mode v2',
        cmp_autodetect: 'Auto-détection CMP (6 fournisseurs)',
        gdpr: 'Conformité GDPR',
        realtime_dashboard: 'Dashboard temps réel',
        emq_monitoring: 'Monitoring EMQ Meta',
        all: 'Toutes les fonctionnalités',
      },
    },
    settings: {
      title: 'Paramètres',
      subtitle: 'Configurez les paramètres de votre boutique',
      shopInfo: 'Informations de la boutique',
      shopDomain: 'Domaine',
      shopName: 'Nom',
      shopId: 'ID Trackful',
      createdAt: 'Créé le',
      consentSettings: 'Paramètres de consentement',
      consentDesc: 'Ce que nous supposons avant que votre bannière de consentement ne réponde, et pour les visiteurs qui ne répondent jamais. Votre CMP reste maître dès qu\'elle a répondu. Attention : « Refusé » est un arrêt net (Meta, TikTok, Pinterest, Snapchat et GA4 ne reçoivent alors rien du tout), alors que « Non spécifié » envoie uniquement les identifiants de clic, sans aucune donnée personnelle.',
      defaultConsent: 'Consentement par défaut',
      adStorage: 'Stockage publicitaire',
      adStorageHelp: 'Autorise le stockage de cookies pour la publicité',
      analyticsStorage: 'Stockage analytics',
      analyticsStorageHelp: 'Autorise le stockage de cookies pour l\'analyse',
      adUserData: 'Données utilisateur pub',
      adUserDataHelp: 'Autorise l\'envoi de données utilisateur pour la publicité',
      adPersonalization: 'Personnalisation pub',
      adPersonalizationHelp: 'Autorise la personnalisation des annonces',
      granted: 'Accordé',
      denied: 'Refusé',
      unspecified: 'Non spécifié (recommandé avec une bannière de consentement)',
      saveConsent: 'Enregistrer le consentement',
      saveFailed: 'Impossible d\'enregistrer',
      language: 'Langue',
      languageHelp: 'Sélectionnez la langue de l\'interface',
      french: 'Français',
      english: 'Anglais',
      german: 'Allemand',
      spanish: 'Espagnol',
      portugueseBR: 'Portugais (Brésil)',
      japanese: 'Japonais',
      dutch: 'Néerlandais',
    },
    backfillHold: {
      titleOne: '{n} commande passée est prête à être envoyée à vos plateformes publicitaires',
      titleMany: '{n} commandes passées sont prêtes à être envoyées à vos plateformes publicitaires',
      action: 'Envoyer à mes plateformes',
      actionPending: 'Envoi en cours...',
      imported: 'Nous avons importé vos 90 derniers jours de commandes lors de l\'installation de Trackful. Elles sont déjà comptées dans ce tableau de bord, ainsi que dans Google Analytics 4.',
      notSent: 'Nous ne les avons pas envoyées à Google Ads, Meta, TikTok, Pinterest ni Snapchat. Ce sont vos comptes publicitaires, et si un autre outil de suivi a déjà remonté ces mêmes commandes, les renvoyer doublerait votre historique. Rien ne part tant que vous ne l\'avez pas décidé.',
      windows: 'Seules les commandes récentes seront acceptées : Meta refuse tout ce qui dépasse 7 jours, et Google Ads environ 90. Les plus anciennes restent dans votre tableau de bord dans tous les cas.',
      error: 'L\'envoi a échoué. Rien n\'a été perdu, vous pouvez réessayer.',
      success: 'Envoyé. Elles apparaîtront sur vos plateformes dans les prochaines minutes.',
    },
    competingPixels: {
      criticalTitle: 'Vos achats sont probablement comptés deux fois sur Meta',
      criticalAction: 'Ouvrir les réglages Meta',
      criticalDetected: 'Nous avons détecté ce qui ressemble à {sources} sur votre boutique. Cet outil envoie ses propres événements d\'achat à Meta, et Trackful envoie les mêmes achats côté serveur. Ces deux sources ne peuvent pas être dédupliquées : le canal de vente choisit ses propres identifiants d\'événement et ne les expose nulle part, donc Meta n\'a aucun moyen de distinguer les deux.',
      criticalAdvice: 'Gardez une seule source, pas les deux. Nous recommandons de garder Trackful et de désactiver le suivi du canal de vente, parce que Trackful remonte aussi les achats qu\'un pixel navigateur perd à cause des bloqueurs de publicité, des restrictions iOS et des paiements hébergés. Si vous préférez garder le canal de vente, désactivez plutôt Meta dans Trackful.',
      warningTitle: 'Un pixel navigateur tourne en parallèle de Trackful',
      warningDetected: 'Nous avons détecté {sources} sur votre boutique. Ce pixel et Trackful atteignent le même compte publicitaire, donc un même achat peut être compté deux fois.',
      warningDedup: 'Ces sources vous appartiennent, la déduplication est donc possible : transmettez l\'identifiant d\'événement de Trackful avec l\'événement navigateur. Pour un achat, notre identifiant est le {strong}.',
      warningDedupStrong: 'numéro de commande',
      itemMeta: 'Pixel Meta : renseignez {code} avec le numéro de commande sur votre événement Purchase navigateur.',
      itemOthers: 'TikTok, Pinterest et Snap : renseignez {code} avec le numéro de commande.',
      itemGtm: 'Google Tag Manager : transmettez le numéro de commande aux balises que le conteneur déclenche.',
      selfDeduping: '{sources} peut rester tel quel : Google Ads déduplique tout seul sur le numéro de commande.',
      lastDetected: 'Dernière détection sur votre boutique : {date}',
    },
  },

  // ===========================================================================
  // ENGLISH
  // ===========================================================================
  en: {
    nav: {
      dashboard: 'Dashboard',
      googleAds: 'Google Ads',
      meta: 'Meta CAPI',
      billing: 'Billing',
      settings: 'Settings',
    },
    common: {
      save: 'Save',
      cancel: 'Cancel',
      connect: 'Connect',
      disconnect: 'Disconnect',
      enable: 'Enable',
      disable: 'Disable',
      test: 'Test',
      refresh: 'Refresh',
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      warning: 'Warning',
      connected: 'Connected',
      notConnected: 'Not Connected',
      active: 'Active',
      disabled: 'Disabled',
      pending: 'Pending',
      sent: 'Sent',
      thisMonth: 'This month',
      selectAccount: 'Select Account',
      selectAction: 'Select Action',
      createNew: 'Create New',
      actionFailed: 'Something went wrong. Please try again.',
      configFailed: 'Configuration failed. Please try again.',
      gateNeedsPlan: 'This feature requires the {plan} plan',
      gateNeedsSubscription: 'Activate your subscription to unlock this feature',
      gateBody: 'Your data capture continues as normal. Upgrade to the {plan} plan to enable this feature in a few clicks.',
      gateCta: 'View plans',
      disableChannelTitle: 'Disable forwarding to {channel}?',
      disableChannelBody: 'Capture keeps running at 100%: every conversion is still recorded in Trackful. Only forwarding to {channel} is paused.',
      disableChannelSafety: 'When you re-enable, every conversion captured during the pause is automatically re-sent to {channel}. A confirmation email will be sent to you.',
    },
    dashboard: {
      title: 'Dashboard',
      totalConversions: 'Total Conversions',
      totalRevenue: 'Total Revenue',
      googleAdsTracked: 'Google Ads Tracked',
      deliveryRate: 'Delivered to ad platforms',
      metaTracked: 'Meta Tracked',
      ofConversions: 'of conversions',
      attributionRate: 'Attribution Rate',
      conversionsWithAttribution: 'Conversions with click ID attribution',
      excellent: 'Excellent',
      good: 'Good',
      needsImprovement: 'Needs Improvement',
      integrationStatus: 'Integration Status',
      eventMatchQuality: 'Meta Event Match Quality',
      emqNotAvailable: 'EMQ data not available. Connect Meta CAPI and send some events.',
      recentConversions: 'Recent Conversions',
      deliveryPerChannel: 'Delivery per channel: every conversion is sent to all active channels; each platform then runs its own attribution.',
      noConversions: 'No conversions yet',
      noConversionsDesc: 'Conversions will appear here once you receive orders with tracking enabled.',
      order: 'Order',
      revenue: 'Revenue',
      googleAds: 'Google Ads',
      meta: 'Meta',
      attribution: 'Attribution',
      date: 'Date',
      warningNotConnected: 'Neither Google Ads nor Meta CAPI are connected. Configure your integrations to start tracking conversions.',
      warningGoogleNotConnected: 'Google Ads is not connected. Configure it to track conversions.',
      warningMetaNotConnected: 'Meta CAPI is not connected. Configure it to track conversions.',
      integrationLoadError: 'Failed to load integration status. Please refresh the page.',
      failedThisMonth: 'Failed this month',
      yes: 'Yes',
      no: 'No',
      direct: 'Direct',
    },
    setup: {
      activeTitle: 'Your enterprise server-side tracking is active',
      activeBadge: 'Active',
      activeBody: 'Nothing else to do. We handle the rest: event capture, PII hashing, delivery to the ad platforms, and self-healing on errors.',
      activeLiveChannels: 'Live channels',
      inactiveTitle: 'Activate your server-side tracking',
      inactiveBody: 'Connect at least one ad platform. One channel is enough to go live: Trackful automatically sets up everything behind the scenes for it (conversion action, pixel, attribution).',
      connect: 'Connect',
      connected: 'Connected',
      needsAction: 'Finish',
      connectFailed: 'Connection failed. Please try again.',
      pixelLive: 'Pixel active, events captured',
      pixelWaiting: 'Waiting for the first pixel events',
      clickTrackerTitle: 'Activate click tracking on your theme',
      clickTrackerActive: 'Active',
      clickTrackerCta: 'Activate now',
      clickTrackerBody: 'The click tracker captures ad identifiers (gclid, fbclid...) and sets 1-year server cookies: the prerequisite for accurate attribution. Shopify cannot enable it automatically: one click on Activate, then Save in the theme editor.',
      clickTrackerHint: 'After saving, open your storefront once: this step will turn green automatically.',
      sendTestEvent: 'Send a test conversion',
      testEventOk: 'Test conversion accepted by Google ✓',
      testEventFail: 'The test failed: open the Google Ads page for details.',
      consentNotice: 'Compliance: without a cookie banner (Consent Mode v2), EU conversions cannot carry customer data to Meta and GA4. Set up your CMP for maximum coverage.',
      advanced: 'Advanced options',
      funnelTitle: 'Full-funnel forwarding',
      funnelBody: 'Sends the whole shopping journey (product views, add-to-carts, checkouts) to your ad platforms as extra optimization signals - not just purchases. Purchases are always forwarded.',
      funnelOn: 'On',
      funnelOff: 'Off',
      funnelChannelsTitle: 'What each platform receives',
      funnelChannelsBody: 'Purchases always go out. The rest of the journey (product views, add-to-carts, checkouts) only goes out when we can guarantee it will not be counted twice.',
      funnelSends: 'Full journey',
      funnelPurchasesOnly: 'Purchases only',
      funnelWhyOwn: 'Our own conversion actions: nothing of yours can be counted twice.',
      funnelWhyStamped: 'Your tag ({sources}) now carries our event id, so the platform counts one event, not two.',
      funnelWhyEmpty: 'The property was receiving nothing before us.',
      funnelWhyDeclared: 'You told us Trackful is your only source here.',
      funnelWhyCollision: 'Another source already feeds this platform ({sources}). We send purchases only, so your numbers are not doubled.',
      funnelAsk: 'Does another tag already send events to this platform from the browser? Shopify native sales channels count.',
      funnelAskKeep: 'Yes, I keep it',
      funnelAskOnly: 'No, Trackful is my only source',
      funnelAskWhy: 'We cannot see this ourselves: Shopify native channel pixels run in an isolated sandbox.',
      pausedTitle: 'Forwarding paused (your data is safe)',
      pausedCta: 'Upgrade',
      pausedBody: 'You have reached your plan limit. We keep capturing everything, but forwarding to your ad platforms is paused. Upgrade to resume - we will automatically re-send everything we captured.',
      pausedBodyCount: 'Plan limit reached. {count} captured conversion(s) waiting to be sent. We keep capturing everything; upgrade to resume forwarding and we backfill it all automatically.',
    },
    googleAds: {
      title: 'Google Ads',
      subtitle: 'Connect Google Ads for Enhanced Conversions tracking',
      connectionStatus: 'Connection Status',
      account: 'Account',
      conversionAction: 'Conversion Action',
      lastSync: 'Last Sync',
      testConnection: 'Test Connection',
      connectGoogleAds: 'Connect Google Ads',
      connectDescription: 'Connect your Google Ads account to start tracking conversions with Enhanced Conversions.',
      enhancedConversions: 'Enhanced Conversions',
      enhancedConversionsDesc: 'Enhanced Conversions improves conversion tracking accuracy by securely sending hashed customer data (email, phone, address) to Google Ads.',
      enableEnhancedConversions: 'Enable Enhanced Conversions',
      enhancedConversionsHelp: 'Requires customer consent for ad_user_data under GDPR/Consent Mode v2',
      ecActionRequiredTitle: 'Action required in Google Ads',
      ecActionRequiredIntro: 'Enhanced Conversions cannot be enabled from this app. You must set it up in Google Ads (Goals > Conversions > Settings):',
      ecStep1: '1. Turn on "Enhanced conversions for web"',
      ecStep2: '2. Accept the customer data terms',
      ecStep3: '3. Choose "Google Ads API" as the method',
      ecNote: 'Until these steps are done, the hashed customer data we send is not processed by Google.',
      ecReady: 'Enhanced Conversions is ready: the customer data terms are accepted on this Google Ads account.',
      conversionActionsTitle: 'Conversion actions',
      conversionActionsDesc: 'Trackful forwards the purchase (primary action) and can also forward funnel steps as secondary actions.',
      autoCreatedNote: 'Created automatically in your Google Ads account when you selected the account. Find them under Goals > Conversions.',
      funnelOffHint: 'Full-funnel forwarding is off (Dashboard > Advanced options): these toggles apply to secondary events once it is enabled. Purchases are always forwarded.',
      disableTitle: 'Disable forwarding to Google Ads?',
      disableConfirmBody: 'Capture keeps running at 100%: every conversion is still recorded in Trackful. Only forwarding to Google Ads is paused.',
      disableConfirmSafety: 'When you re-enable, every conversion captured during the pause is automatically re-sent to Google Ads. A confirmation email will be sent to you.',
      funnelPurchase: 'Purchase',
      funnelPurchaseDesc: 'The most important event: the sale. Always forwarded.',
      primaryBadge: 'Primary',
      createPrimaryAction: 'Create purchase action',
      activateAction: 'Activate',
      funnelProductView: 'Product view',
      funnelProductViewDesc: 'When a visitor views a product page.',
      funnelAddToCart: 'Add to cart',
      funnelAddToCartDesc: 'When a product is added to the cart. Strong intent signal.',
      funnelBeginCheckout: 'Begin checkout',
      funnelBeginCheckoutDesc: 'When a visitor starts checkout.',
      funnelAddPaymentInfo: 'Add payment info',
      funnelAddPaymentInfoDesc: 'When payment information is entered.',
      setupInstructions: 'Setup Instructions',
      step1: '1. Click "Connect Google Ads" to authorize access',
      step2: '2. Select the Google Ads account to use',
      step3: '3. Choose or create a conversion action for purchases',
      step4: '4. Enable Enhanced Conversions for better accuracy',
      selectAccountTitle: 'Select Google Ads Account',
      availableAccounts: 'Available Accounts',
      noAccounts: 'No accounts found. Make sure your Google account has access to at least one Google Ads account.',
      selectAccountPrompt: 'No account selected yet. Choose the Google Ads account to track.',
      selectConversionTitle: 'Select Conversion Action',
      selectConversionDesc: 'Select an existing conversion action or create a new one for tracking purchases.',
      availableActions: 'Available Conversion Actions',
      noActions: 'No conversion actions found. Click "Create New" to create one.',
      tagBasedAction: 'Tag-based action: cannot receive server-side uploads. Pick a Trackful action or create one.',
      changeAction: 'Change action',
      conflictTitle: 'Double counting risk detected',
      conflictDesc: 'These other Purchase conversion actions are also set as Primary in your Google Ads account. While multiple purchase actions stay Primary, your purchases are counted twice in the Conversions column and in bidding:',
      conflictFix: 'Make Trackful the only primary',
      conflictKeep: 'Keep as is',
      conflictConfirmDesc: 'The actions below will be set to Secondary (observation) in your Google Ads account. They stay tracked but no longer count in the Conversions column or in bidding. Reversible at any time.',
      conflictFixed: 'Done. The following actions are now observation-only:',
      conflictUndo: 'Undo',
      disconnectTitle: 'Disconnect Google Ads',
      disconnectConfirm: 'Are you sure you want to disconnect Google Ads? This will stop tracking conversions until you reconnect.',
      disconnectRemoveActions: 'Also remove the conversion actions Trackful created in Google Ads',
      disconnectRemoveActionsHelp: 'They will be set to Removed: they stop recording, but your historical data stays in your reports.',
      testSuccess: 'Test successful! Configuration is valid.',
      testRequestId: 'Google request ID',
      testFailed: 'Test failed',
      enableTracking: 'Enable Tracking',
      disableTracking: 'Disable Tracking',
      actionFailed: 'An error occurred. Please try again.',
    },
    meta: {
      title: 'Meta CAPI',
      subtitle: 'Configure Meta Conversions API for server-side tracking',
      connectionStatus: 'Connection Status',
      pixelId: 'Pixel ID',
      testEventCode: 'Test Event Code',
      lastSync: 'Last Sync',
      sendTestEvent: 'Send Test Event',
      updateConfiguration: 'Update Configuration',
      configureMeta: 'Configure Meta CAPI',
      connectDescription: 'Connect your Meta Pixel to start tracking conversions via the Conversions API.',
      emq: 'Event Match Quality (EMQ)',
      emqDesc: 'EMQ measures how well your event data matches Meta users. Higher scores mean better attribution and ad performance.',
      overallScore: 'Overall Score',
      recommendations: 'Recommendations',
      emqNotAvailable: 'EMQ data not available. Send some events and refresh to see scores.',
      setupInstructions: 'Setup Instructions',
      step1: '1. Go to your Meta Events Manager',
      step2: '2. Find your Pixel ID (Settings > Pixel ID)',
      step3: '3. Generate an Access Token (Settings > Conversions API > Generate Token)',
      step4: '4. Optionally, create a Test Event Code for debugging',
      step5: '5. Enter these values in the configuration form',
      configureTitle: 'Configure Meta CAPI',
      saveConfiguration: 'Save Configuration',
      pixelIdPlaceholder: '1234567890123456',
      pixelIdHelp: 'Your Meta Pixel ID from Events Manager',
      accessToken: 'Access Token',
      accessTokenPlaceholder: 'EAAx...',
      accessTokenHelp: 'Conversions API access token (keep this secret)',
      testEventCodeOptional: 'Test Event Code (Optional)',
      testEventCodePlaceholder: 'TEST12345',
      testEventCodeHelp: 'Use this to send events to Test Events in Events Manager',
      disconnectTitle: 'Disconnect Meta CAPI',
      disconnectConfirm: 'Are you sure you want to disconnect Meta CAPI? This will stop tracking conversions until you reconnect.',
      testSuccess: 'Test event sent successfully! Events received:',
      testFailed: 'Test failed',
      required: 'Pixel ID and Access Token are required',
      enableTracking: 'Enable Tracking',
      disableTracking: 'Disable Tracking',
      actionFailed: 'An error occurred. Please try again.',
    },
    billing: {
      title: 'Billing',
      subtitle: 'Manage your subscription and track your usage',
      currentPlan: 'Current Plan',
      usage: 'Usage',
      ordersUsed: 'Orders Tracked',
      ordersRemaining: 'Events Remaining',
      unlimited: 'Unlimited',
      ofLimit: 'of limit',
      trial: 'Free Trial',
      trialActive: 'Trial Active',
      trialEndsIn: 'Trial ends in',
      trialDaysRemaining: 'remaining',
      days: 'days',
      upgradeNow: 'Upgrade Now',
      upgradePlan: 'Upgrade Plan',
      downgradePlan: 'Downgrade Plan',
      alreadyCancelled: 'Cancelled. Access runs to the end of the period.',
      cancelSubscription: 'Cancel Subscription',
      scheduledChange: 'Changing to {plan} on {date}. Until then you keep {current}.',
      // Billing cadence. `savePercent` and `billedYearly` are templates:
      // the numbers come from the API so the page can never quote a
      // discount the charge does not match.
      billMonthly: 'Monthly',
      billAnnually: 'Yearly',
      savePercent: 'Save {percent}%',
      billedYearly: 'billed {amount} per year',
      switchToYearly: 'Switch to yearly',
      switchToMonthly: 'Switch to monthly',
      selectPlan: 'Select This Plan',
      currentPlanBadge: 'Current Plan',
      month: '/month',
      ordersPerMonth: 'tracked orders/month',
      subscribe: 'Subscribe',
      havePartnerCode: 'Have a partner code?',
      partnerCodeApplied: 'Partner code applied',
      partnerCodeAppliedHelp: 'Your discount is applied before you confirm, so the price Shopify shows you already includes it.',
      partnerCodePrompt: 'If a partner gave you a code, enter it before subscribing.',
      partnerCodeFirstOnly: 'A partner code is entered before your first subscription. A discount already running carries over when you change plan, as long as you stay on the same billing cycle.',
      partnerCodePlaceholder: 'Optional',
      partnerCodeLabel: 'Partner code',
      partnerCodeHelp: 'Applied to your first month. An unknown code simply means no discount.',
      sectionChannels: 'Ad channels',
      sectionTracking: 'Tracking & attribution',
      sectionCompliance: 'Compliance & consent',
      sectionInsights: 'Data & insights',
      sectionSupport: 'Support',
      features: 'Features',
      plans: 'Available Plans',
      starter: 'Starter',
      growth: 'Growth',
      pro: 'Pro',
      warningNearLimit: 'You are approaching your monthly event limit.',
      warningOverLimit: 'You have exceeded your event limit. Upgrade to continue tracking.',
      warningTrialEnding: 'Your trial is ending soon. Subscribe to continue.',
      billingHistory: 'Billing History',
      noHistory: 'No billing history',
      recommendation: 'Recommendation',
      upgradeToRecommended: 'Upgrade to {plan}',
      plansLoadError: 'Failed to load plans. Please refresh the page.',
      actionFailed: 'Failed to process plan action. Please try again.',
      subscriptionSuccess: 'Subscription activated successfully! Your tracking is now live.',
      planNowSuccess: 'Done. You are now on the {plan} plan.',
      scheduledSuccess: 'Confirmed. You move to {plan} at the end of the period you have already paid for.',
      scheduledBadge: 'Scheduled',
      cancelConfirmationTrial: 'You are still in your free trial, so nothing has been charged. Cancelling puts you back on the Free plan right away.',
      activationFailed: 'We could not confirm your subscription. Please try again or contact support.',
      planName: 'Plan',
      priceLabel: 'Price',
      upgrade: 'Upgrade',
      downgrade: 'Downgrade',
      historyDate: 'Date',
      historyType: 'Type',
      historyAmount: 'Amount',
      historyStatus: 'Status',
      cancelTitle: 'Cancel subscription',
      confirmCancel: 'Yes, cancel',
      keepPlan: 'Keep my plan',
      cancelConfirmation: 'Are you sure you want to cancel? Your plan remains active until the end of the current billing period.',
      featureLabels: {
        google_ads: 'Google Ads Enhanced Conversions',
        meta_capi: 'Meta Conversions API',
        identity_resolution: 'Identity Resolution',
        basic_support: 'Basic Support',
        priority_support: 'Priority Support',
        dedicated_support: 'Dedicated Support',
        advanced_analytics: 'Advanced Analytics',
        api_access: 'API Access',
        ga4: 'Google Analytics 4 (Measurement Protocol)',
        tiktok: 'TikTok Events API',
        pinterest: 'Pinterest Conversions API',
        snapchat: 'Snapchat Conversions API',
        reconciliation: 'Conversion reconciliation',
        live_debugger: 'Live event debugger',
        alerts: 'Tracking alerts',
        export_csv: 'CSV export',
        webhooks_out: 'Outgoing webhooks',
        server_side_tracking: 'Server-side tracking (bypasses ad blockers)',
        one_year_cookies: '1-year server cookies (Safari ITP bypass)',
        click_id_capture: 'Click ID capture (gclid, fbclid, ttclid, etc.)',
        full_funnel: 'Full-funnel forwarding (views, carts, checkouts)',
        dedup: 'Deterministic event deduplication',
        auto_backfill: 'Automatic backfill after a pause (quota, disabled channel)',
        consent_mode: 'Google Consent Mode v2',
        cmp_autodetect: 'CMP auto-detection (6 providers)',
        gdpr: 'GDPR compliant',
        realtime_dashboard: 'Real-time dashboard',
        emq_monitoring: 'Meta EMQ monitoring',
        all: 'All Features',
      },
    },
    settings: {
      title: 'Settings',
      subtitle: 'Configure your shop settings',
      shopInfo: 'Shop Information',
      shopDomain: 'Domain',
      shopName: 'Name',
      shopId: 'Trackful ID',
      createdAt: 'Created',
      consentSettings: 'Consent Settings',
      consentDesc: 'What we assume before your consent banner answers, and for visitors who never answer. Your CMP takes over the moment it does. Note: "Denied" is a hard stop (Meta, TikTok, Pinterest, Snapchat and GA4 receive nothing at all), while "Not specified" sends click IDs only, with no personal data.',
      defaultConsent: 'Default Consent',
      adStorage: 'Ad Storage',
      adStorageHelp: 'Allows storage of cookies for advertising',
      analyticsStorage: 'Analytics Storage',
      analyticsStorageHelp: 'Allows storage of cookies for analytics',
      adUserData: 'Ad User Data',
      adUserDataHelp: 'Allows sending user data for advertising',
      adPersonalization: 'Ad Personalization',
      adPersonalizationHelp: 'Allows personalization of ads',
      granted: 'Granted',
      denied: 'Denied',
      unspecified: 'Not specified (recommended if you use a consent banner)',
      saveConsent: 'Save Consent',
      saveFailed: 'Failed to save',
      language: 'Language',
      languageHelp: 'Select interface language',
      french: 'French',
      english: 'English',
      german: 'German',
      spanish: 'Spanish',
      portugueseBR: 'Portuguese (Brazil)',
      japanese: 'Japanese',
      dutch: 'Dutch',
    },
    backfillHold: {
      titleOne: '{n} past order is ready to send to your ad platforms',
      titleMany: '{n} past orders are ready to send to your ad platforms',
      action: 'Send to my ad platforms',
      actionPending: 'Sending...',
      imported: 'We imported your last 90 days of orders when you installed Trackful. They are already counted in this dashboard, and in Google Analytics 4.',
      notSent: 'We have not sent them to Google Ads, Meta, TikTok, Pinterest or Snapchat. Those are your ad accounts, and if another tracking tool already reported these same orders, sending them again would double your history. Nothing is sent until you choose to.',
      windows: 'Only recent orders will be accepted: Meta refuses anything older than 7 days, and Google Ads about 90. Older orders stay in your dashboard either way.',
      error: 'Something went wrong sending them. Nothing was lost, you can try again.',
      success: 'Sent. They will appear in your ad platforms over the next few minutes.',
    },
    competingPixels: {
      criticalTitle: 'Your purchases are likely counted twice on Meta',
      criticalAction: 'Open Meta settings',
      criticalDetected: 'We detected what looks like {sources} on your storefront. It sends its own purchase events to Meta, and Trackful sends the same purchases server-side. Those two sources cannot be deduplicated: the sales channel picks its own event IDs and does not expose them, so Meta has no way to tell the two events apart.',
      criticalAdvice: 'Keep one source, not both. We recommend keeping Trackful and turning off the sales channel\'s tracking, because Trackful still reports the purchases a browser pixel loses to ad blockers, iOS tracking restrictions, and hosted checkouts. If you would rather keep the sales channel, disable Meta in Trackful instead.',
      warningTitle: 'A browser pixel is running alongside Trackful',
      warningDetected: 'We detected {sources} on your storefront. Both that pixel and Trackful reach the same ad account, so the same purchase can be counted twice.',
      warningDedup: 'These sources are yours to configure, so deduplication is possible: send Trackful\'s event ID with the browser event. For a purchase, our event ID is the {strong}.',
      warningDedupStrong: 'order ID',
      itemMeta: 'Meta Pixel: set {code} to the order ID on your browser Purchase event.',
      itemOthers: 'TikTok, Pinterest and Snap: set {code} to the order ID.',
      itemGtm: 'Google Tag Manager: pass the order ID into whichever vendor tags the container fires.',
      selfDeduping: '{sources} is fine to keep as is: Google Ads deduplicates on the order ID by itself.',
      lastDetected: 'Last detected on your storefront: {date}',
    },
  },

  // ===========================================================================
  // DEUTSCH (German)
  // ===========================================================================
  de: {
    nav: {
      dashboard: 'Dashboard',
      googleAds: 'Google Ads',
      meta: 'Meta CAPI',
      billing: 'Abrechnung',
      settings: 'Einstellungen',
    },
    common: {
      save: 'Speichern',
      cancel: 'Abbrechen',
      connect: 'Verbinden',
      disconnect: 'Trennen',
      enable: 'Aktivieren',
      disable: 'Deaktivieren',
      test: 'Testen',
      refresh: 'Aktualisieren',
      loading: 'Laden...',
      error: 'Fehler',
      success: 'Erfolg',
      warning: 'Warnung',
      connected: 'Verbunden',
      notConnected: 'Nicht verbunden',
      active: 'Aktiv',
      disabled: 'Deaktiviert',
      pending: 'Ausstehend',
      sent: 'Gesendet',
      thisMonth: 'Diesen Monat',
      selectAccount: 'Konto auswählen',
      selectAction: 'Aktion auswählen',
      createNew: 'Neu erstellen',
      actionFailed: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
      configFailed: 'Konfiguration fehlgeschlagen. Bitte versuchen Sie es erneut.',
      gateNeedsPlan: 'Diese Funktion erfordert den {plan}-Plan',
      gateNeedsSubscription: 'Aktivieren Sie Ihr Abonnement, um diese Funktion freizuschalten',
      gateBody: 'Ihre Datenerfassung läuft normal weiter. Wechseln Sie zum {plan}-Plan, um diese Funktion mit wenigen Klicks zu aktivieren.',
      gateCta: 'Pläne ansehen',
      disableChannelTitle: 'Übertragung an {channel} deaktivieren?',
      disableChannelBody: 'Die Erfassung läuft zu 100% weiter: Jede Conversion wird weiterhin in Trackful gespeichert. Nur die Übertragung an {channel} wird pausiert.',
      disableChannelSafety: 'Bei der Reaktivierung werden alle während der Pause erfassten Conversions automatisch erneut an {channel} gesendet. Sie erhalten eine Bestätigungs-E-Mail.',
    },
    dashboard: {
      title: 'Dashboard',
      totalConversions: 'Conversions gesamt',
      totalRevenue: 'Gesamtumsatz',
      googleAdsTracked: 'Google Ads erfasst',
      deliveryRate: 'An Werbeplattformen gesendet',
      metaTracked: 'Meta erfasst',
      ofConversions: 'der Conversions',
      attributionRate: 'Zuordnungsrate',
      conversionsWithAttribution: 'Conversions mit Click-ID-Zuordnung',
      excellent: 'Ausgezeichnet',
      good: 'Gut',
      needsImprovement: 'Verbesserungsbedarf',
      integrationStatus: 'Integrationsstatus',
      eventMatchQuality: 'Meta Event Match Quality',
      emqNotAvailable: 'EMQ-Daten nicht verfügbar. Verbinden Sie Meta CAPI und senden Sie Events.',
      recentConversions: 'Letzte Conversions',
      deliveryPerChannel: 'Zustellung pro Kanal: Jede Conversion wird an alle aktiven Kanäle gesendet; jede Plattform führt anschließend ihre eigene Attribution durch.',
      noConversions: 'Keine Conversions',
      noConversionsDesc: 'Conversions werden hier angezeigt, sobald Sie Bestellungen mit aktiviertem Tracking erhalten.',
      order: 'Bestellung',
      revenue: 'Umsatz',
      googleAds: 'Google Ads',
      meta: 'Meta',
      attribution: 'Zuordnung',
      date: 'Datum',
      warningNotConnected: 'Weder Google Ads noch Meta CAPI sind verbunden. Konfigurieren Sie Ihre Integrationen, um das Tracking zu starten.',
      warningGoogleNotConnected: 'Google Ads ist nicht verbunden. Konfigurieren Sie es, um Conversions zu erfassen.',
      warningMetaNotConnected: 'Meta CAPI ist nicht verbunden. Konfigurieren Sie es, um Conversions zu erfassen.',
      integrationLoadError: 'Integrationsstatus konnte nicht geladen werden. Bitte aktualisieren Sie die Seite.',
      failedThisMonth: 'Fehlgeschlagen diesen Monat',
      yes: 'Ja',
      no: 'Nein',
      direct: 'Direkt',
    },
    setup: {
      activeTitle: 'Ihr serverseitiges Enterprise-Tracking ist aktiv',
      activeBadge: 'Aktiv',
      activeBody: 'Nichts weiter zu tun. Wir kümmern uns um den Rest: Ereigniserfassung, Hashing personenbezogener Daten, Übermittlung an die Werbeplattformen und Selbstheilung bei Fehlern.',
      activeLiveChannels: 'Aktive Kanäle',
      inactiveTitle: 'Aktivieren Sie Ihr serverseitiges Tracking',
      inactiveBody: 'Verbinden Sie mindestens eine Werbeplattform. Ein Kanal genügt zum Start: Trackful richtet die gesamte Technik dahinter automatisch ein (Conversion-Aktion, Pixel, Attribution).',
      connect: 'Verbinden',
      connected: 'Verbunden',
      needsAction: 'Abschließen',
      connectFailed: 'Verbindung fehlgeschlagen. Bitte erneut versuchen.',
      pixelLive: 'Pixel aktiv, Ereignisse erfasst',
      pixelWaiting: 'Warten auf die ersten Pixel-Ereignisse',
      clickTrackerTitle: 'Aktivieren Sie das Klick-Tracking in Ihrem Theme',
      clickTrackerActive: 'Aktiv',
      clickTrackerCta: 'Jetzt aktivieren',
      clickTrackerBody: 'Der Klick-Tracker erfasst Werbe-IDs (gclid, fbclid...) und setzt Server-Cookies mit 1 Jahr Laufzeit: die Voraussetzung für präzise Attribution. Shopify kann ihn nicht automatisch aktivieren: ein Klick auf Aktivieren, dann Speichern im Theme-Editor.',
      clickTrackerHint: 'Nach dem Speichern öffnen Sie Ihren Shop einmal: dieser Schritt wird automatisch grün.',
      sendTestEvent: 'Test-Conversion senden',
      testEventOk: 'Test-Conversion von Google akzeptiert ✓',
      testEventFail: 'Der Test ist fehlgeschlagen: Details auf der Google Ads-Seite.',
      consentNotice: 'Compliance: Ohne Cookie-Banner (Consent Mode v2) können EU-Conversions keine Kundendaten an Meta und GA4 übertragen. Richten Sie Ihre CMP ein.',
      advanced: 'Erweiterte Optionen',
      funnelTitle: 'Weiterleitung des gesamten Funnels',
      funnelBody: 'Sendet die gesamte Customer Journey (Produktaufrufe, Warenkorb, Checkouts) als zusätzliche Optimierungssignale an Ihre Werbeplattformen - nicht nur Käufe. Käufe werden immer übertragen.',
      funnelOn: 'Ein',
      funnelOff: 'Aus',
      funnelChannelsTitle: 'Was jede Plattform erhält',
      funnelChannelsBody: 'Käufe werden immer gesendet. Der Rest der Journey (Produktansichten, Warenkorb, Checkout) nur dann, wenn wir garantieren können, dass er nicht doppelt gezählt wird.',
      funnelSends: 'Gesamte Journey',
      funnelPurchasesOnly: 'Nur Käufe',
      funnelWhyOwn: 'Unsere eigenen Conversion-Aktionen: nichts von Ihnen kann doppelt gezählt werden.',
      funnelWhyStamped: 'Ihr Tag ({sources}) trägt jetzt unsere Event-ID, die Plattform zählt also ein Event statt zwei.',
      funnelWhyEmpty: 'Die Property hat vor uns nichts empfangen.',
      funnelWhyDeclared: 'Sie haben uns gesagt, dass Trackful hier Ihre einzige Quelle ist.',
      funnelWhyCollision: 'Eine andere Quelle speist diese Plattform bereits ({sources}). Wir senden nur Käufe, damit Ihre Zahlen nicht verdoppelt werden.',
      funnelAsk: 'Sendet ein anderes Tag bereits Events aus dem Browser an diese Plattform? Native Shopify-Vertriebskanäle zählen dazu.',
      funnelAskKeep: 'Ja, ich behalte es',
      funnelAskOnly: 'Nein, Trackful ist meine einzige Quelle',
      funnelAskWhy: 'Wir können das nicht selbst sehen: Pixel nativer Shopify-Kanäle laufen in einer isolierten Sandbox.',
      pausedTitle: 'Weiterleitung pausiert (Ihre Daten sind sicher)',
      pausedCta: 'Upgrade',
      pausedBody: 'Sie haben Ihr Planlimit erreicht. Wir erfassen weiterhin alles, aber die Weiterleitung an Ihre Werbeplattformen ist pausiert. Führen Sie ein Upgrade durch, um sie fortzusetzen - wir senden automatisch alles Erfasste erneut.',
      pausedBodyCount: 'Planlimit erreicht. {count} erfasste Conversion(s) warten auf den Versand. Wir erfassen weiterhin alles; mit einem Upgrade wird die Weiterleitung fortgesetzt und alles automatisch nachgesendet.',
    },
    googleAds: {
      title: 'Google Ads',
      subtitle: 'Verbinden Sie Google Ads für Enhanced Conversions Tracking',
      connectionStatus: 'Verbindungsstatus',
      account: 'Konto',
      conversionAction: 'Conversion-Aktion',
      lastSync: 'Letzte Synchronisierung',
      testConnection: 'Verbindung testen',
      connectGoogleAds: 'Google Ads verbinden',
      connectDescription: 'Verbinden Sie Ihr Google Ads-Konto, um mit dem Conversion-Tracking via Enhanced Conversions zu beginnen.',
      enhancedConversions: 'Enhanced Conversions',
      enhancedConversionsDesc: 'Enhanced Conversions verbessert die Tracking-Genauigkeit durch sicheres Senden gehashter Kundendaten (E-Mail, Telefon, Adresse) an Google Ads.',
      enableEnhancedConversions: 'Enhanced Conversions aktivieren',
      enhancedConversionsHelp: 'Erfordert Kundeneinwilligung für ad_user_data gemäß DSGVO/Consent Mode v2',
      ecActionRequiredTitle: 'Aktion in Google Ads erforderlich',
      ecActionRequiredIntro: 'Enhanced Conversions kann nicht über diese App aktiviert werden. Sie müssen es in Google Ads einrichten (Ziele > Conversions > Einstellungen):',
      ecStep1: '1. Aktivieren Sie „Enhanced conversions for web“',
      ecStep2: '2. Akzeptieren Sie die Bedingungen zu Kundendaten',
      ecStep3: '3. Wählen Sie „Google Ads API“ als Methode',
      ecNote: 'Bis diese Schritte erledigt sind, werden die von uns gesendeten gehashten Kundendaten von Google nicht verarbeitet.',
      ecReady: 'Enhanced Conversions ist bereit: Die Bedingungen zu Kundendaten sind für dieses Google Ads-Konto akzeptiert.',
      conversionActionsTitle: 'Conversion-Aktionen',
      conversionActionsDesc: 'Trackful übermittelt den Kauf (primäre Aktion) und kann auch Funnel-Schritte als sekundäre Aktionen übermitteln.',
      autoCreatedNote: 'Automatisch in Ihrem Google Ads-Konto erstellt, als Sie das Konto ausgewählt haben. Zu finden unter Ziele > Conversions.',
      funnelOffHint: 'Die Funnel-Weiterleitung ist deaktiviert (Dashboard > Erweiterte Optionen): Diese Schalter gelten für sekundäre Ereignisse, sobald sie aktiviert ist. Käufe werden immer übertragen.',
      disableTitle: 'Übertragung an Google Ads deaktivieren?',
      disableConfirmBody: 'Die Erfassung läuft zu 100% weiter: Jede Conversion wird weiterhin in Trackful gespeichert. Nur die Übertragung an Google Ads wird pausiert.',
      disableConfirmSafety: 'Bei der Reaktivierung werden alle während der Pause erfassten Conversions automatisch erneut an Google Ads gesendet. Sie erhalten eine Bestätigungs-E-Mail.',
      funnelPurchase: 'Kauf',
      funnelPurchaseDesc: 'Das wichtigste Ereignis: der Verkauf. Wird immer übermittelt.',
      primaryBadge: 'Primär',
      createPrimaryAction: 'Kauf-Aktion erstellen',
      activateAction: 'Aktivieren',
      funnelProductView: 'Produktansicht',
      funnelProductViewDesc: 'Wenn ein Besucher eine Produktseite ansieht.',
      funnelAddToCart: 'In den Warenkorb',
      funnelAddToCartDesc: 'Wenn ein Produkt in den Warenkorb gelegt wird. Starkes Kaufsignal.',
      funnelBeginCheckout: 'Checkout gestartet',
      funnelBeginCheckoutDesc: 'Wenn ein Besucher den Checkout startet.',
      funnelAddPaymentInfo: 'Zahlungsinfos hinzugefügt',
      funnelAddPaymentInfoDesc: 'Wenn Zahlungsinformationen eingegeben werden.',
      setupInstructions: 'Einrichtungsanleitung',
      step1: '1. Klicken Sie auf "Google Ads verbinden", um den Zugriff zu autorisieren',
      step2: '2. Wählen Sie das zu verwendende Google Ads-Konto aus',
      step3: '3. Wählen oder erstellen Sie eine Conversion-Aktion für Käufe',
      step4: '4. Aktivieren Sie Enhanced Conversions für bessere Genauigkeit',
      selectAccountTitle: 'Google Ads-Konto auswählen',
      availableAccounts: 'Verfügbare Konten',
      noAccounts: 'Keine Konten gefunden. Stellen Sie sicher, dass Ihr Google-Konto Zugriff auf mindestens ein Google Ads-Konto hat.',
      selectAccountPrompt: 'Noch kein Konto ausgewählt. Wählen Sie das zu verfolgende Google Ads-Konto.',
      selectConversionTitle: 'Conversion-Aktion auswählen',
      selectConversionDesc: 'Wählen Sie eine bestehende Conversion-Aktion oder erstellen Sie eine neue für das Kauf-Tracking.',
      availableActions: 'Verfügbare Conversion-Aktionen',
      noActions: 'Keine Conversion-Aktionen gefunden. Klicken Sie auf "Neu erstellen".',
      tagBasedAction: 'Tag-basierte Aktion: kann keine serverseitigen Uploads empfangen. Wählen Sie eine Trackful-Aktion oder erstellen Sie eine.',
      changeAction: 'Aktion ändern',
      conflictTitle: 'Risiko der Doppelzählung erkannt',
      conflictDesc: 'Diese anderen Kauf-Conversion-Aktionen sind in Ihrem Google Ads-Konto ebenfalls als Primär festgelegt. Solange mehrere Kauf-Aktionen primär sind, werden Ihre Käufe in der Spalte Conversions und beim Bieten doppelt gezählt:',
      conflictFix: 'Trackful zur einzigen primären Aktion machen',
      conflictKeep: 'So belassen',
      conflictConfirmDesc: 'Die folgenden Aktionen werden in Ihrem Google Ads-Konto auf Sekundär (Beobachtung) gesetzt. Sie werden weiter erfasst, zählen aber nicht mehr in der Spalte Conversions oder beim Bieten. Jederzeit umkehrbar.',
      conflictFixed: 'Fertig. Die folgenden Aktionen sind jetzt nur noch zur Beobachtung:',
      conflictUndo: 'Rückgängig',
      disconnectTitle: 'Google Ads trennen',
      disconnectConfirm: 'Sind Sie sicher, dass Sie Google Ads trennen möchten? Das Conversion-Tracking wird bis zur erneuten Verbindung gestoppt.',
      disconnectRemoveActions: 'Auch die von Trackful erstellten Conversion-Aktionen in Google Ads entfernen',
      disconnectRemoveActionsHelp: 'Sie werden auf „Entfernt“ gesetzt: Sie zeichnen nicht mehr auf, aber Ihre historischen Daten bleiben in den Berichten.',
      testSuccess: 'Test erfolgreich! Die Konfiguration ist gültig.',
      testRequestId: 'Google-Anfrage-ID',
      testFailed: 'Test fehlgeschlagen',
      enableTracking: 'Tracking aktivieren',
      disableTracking: 'Tracking deaktivieren',
      actionFailed: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
    },
    meta: {
      title: 'Meta CAPI',
      subtitle: 'Konfigurieren Sie Meta Conversions API für serverseitiges Tracking',
      connectionStatus: 'Verbindungsstatus',
      pixelId: 'Pixel-ID',
      testEventCode: 'Test-Event-Code',
      lastSync: 'Letzte Synchronisierung',
      sendTestEvent: 'Test-Event senden',
      updateConfiguration: 'Konfiguration ändern',
      configureMeta: 'Meta CAPI konfigurieren',
      connectDescription: 'Verbinden Sie Ihr Meta Pixel, um mit dem Conversion-Tracking über die Conversions API zu beginnen.',
      emq: 'Event Match Quality (EMQ)',
      emqDesc: 'Der EMQ misst, wie gut Ihre Event-Daten mit Meta-Nutzern übereinstimmen. Höhere Werte bedeuten bessere Zuordnung und Werbeleistung.',
      overallScore: 'Gesamtbewertung',
      recommendations: 'Empfehlungen',
      emqNotAvailable: 'EMQ-Daten nicht verfügbar. Senden Sie Events und aktualisieren Sie, um die Bewertungen zu sehen.',
      setupInstructions: 'Einrichtungsanleitung',
      step1: '1. Gehen Sie zu Ihrem Meta Events Manager',
      step2: '2. Finden Sie Ihre Pixel-ID (Einstellungen > Pixel-ID)',
      step3: '3. Generieren Sie ein Zugriffstoken (Einstellungen > Conversions API > Token generieren)',
      step4: '4. Erstellen Sie optional einen Test-Event-Code zum Debuggen',
      step5: '5. Geben Sie diese Werte im Konfigurationsformular ein',
      configureTitle: 'Meta CAPI konfigurieren',
      saveConfiguration: 'Konfiguration speichern',
      pixelIdPlaceholder: '1234567890123456',
      pixelIdHelp: 'Ihre Meta Pixel-ID aus dem Events Manager',
      accessToken: 'Zugriffstoken',
      accessTokenPlaceholder: 'EAAx...',
      accessTokenHelp: 'Conversions API Zugriffstoken (geheim halten)',
      testEventCodeOptional: 'Test-Event-Code (Optional)',
      testEventCodePlaceholder: 'TEST12345',
      testEventCodeHelp: 'Verwenden Sie dies, um Events an Test Events im Events Manager zu senden',
      disconnectTitle: 'Meta CAPI trennen',
      disconnectConfirm: 'Sind Sie sicher, dass Sie Meta CAPI trennen möchten? Das Conversion-Tracking wird bis zur erneuten Verbindung gestoppt.',
      testSuccess: 'Test-Event erfolgreich gesendet! Empfangene Events:',
      testFailed: 'Test fehlgeschlagen',
      required: 'Pixel-ID und Zugriffstoken sind erforderlich',
      enableTracking: 'Tracking aktivieren',
      disableTracking: 'Tracking deaktivieren',
      actionFailed: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
    },
    billing: {
      title: 'Abrechnung',
      subtitle: 'Verwalten Sie Ihr Abo und überwachen Sie Ihre Nutzung',
      currentPlan: 'Aktueller Plan',
      usage: 'Nutzung',
      ordersUsed: 'Erfasste Bestellungen',
      ordersRemaining: 'Verbleibende Events',
      unlimited: 'Unbegrenzt',
      ofLimit: 'des Limits',
      trial: 'Kostenlose Testphase',
      trialActive: 'Testphase aktiv',
      trialEndsIn: 'Testphase endet in',
      trialDaysRemaining: 'verbleibend',
      days: 'Tagen',
      upgradeNow: 'Jetzt upgraden',
      upgradePlan: 'Plan upgraden',
      alreadyCancelled: 'Gekündigt. Zugriff bis zum Ende des Zeitraums.',
      downgradePlan: 'Plan downgraden',
      cancelSubscription: 'Abo kündigen',
      scheduledChange: 'Wechsel zu {plan} am {date}. Bis dahin behalten Sie {current}.',
      // Billing cadence. `savePercent` and `billedYearly` are templates:
      // the numbers come from the API so the page can never quote a
      // discount the charge does not match.
      billMonthly: 'Monatlich',
      billAnnually: 'Jährlich',
      savePercent: '{percent} % sparen',
      billedYearly: '{amount} pro Jahr abgerechnet',
      switchToYearly: 'Auf jährlich wechseln',
      switchToMonthly: 'Auf monatlich wechseln',
      selectPlan: 'Diesen Plan wählen',
      currentPlanBadge: 'Aktueller Plan',
      month: '/Monat',
      ordersPerMonth: 'erfasste Bestellungen/Monat',
      subscribe: 'Abonnieren',
      havePartnerCode: 'Haben Sie einen Partnercode?',
      partnerCodeApplied: 'Partnercode angewendet',
      partnerCodeAppliedHelp: 'Ihr Rabatt wird vor Ihrer Bestätigung angewendet, der von Shopify angezeigte Preis enthält ihn also bereits.',
      partnerCodePrompt: 'Wenn Ihnen ein Partner einen Code gegeben hat, geben Sie ihn vor dem Abonnieren ein.',
      partnerCodeFirstOnly: 'Ein Partnercode wird vor dem ersten Abonnement eingegeben. Ein laufender Rabatt bleibt bei einem Tarifwechsel erhalten, solange der Abrechnungszeitraum gleich bleibt.',
      partnerCodePlaceholder: 'Optional',
      partnerCodeLabel: 'Partnercode',
      partnerCodeHelp: 'Gilt für Ihren ersten Monat. Ein unbekannter Code bedeutet einfach keinen Rabatt.',
      sectionChannels: 'Werbekanäle',
      sectionTracking: 'Tracking & Attribution',
      sectionCompliance: 'Compliance & Einwilligung',
      sectionInsights: 'Daten & Einblicke',
      sectionSupport: 'Support',
      features: 'Funktionen',
      plans: 'Verfügbare Pläne',
      starter: 'Starter',
      growth: 'Growth',
      pro: 'Pro',
      warningNearLimit: 'Sie nähern sich Ihrem monatlichen Event-Limit.',
      warningOverLimit: 'Sie haben Ihr Event-Limit überschritten. Upgraden Sie, um das Tracking fortzusetzen.',
      warningTrialEnding: 'Ihre Testphase endet bald. Abonnieren Sie, um fortzufahren.',
      billingHistory: 'Abrechnungsverlauf',
      noHistory: 'Kein Abrechnungsverlauf',
      recommendation: 'Empfehlung',
      upgradeToRecommended: 'Upgrade auf {plan}',
      plansLoadError: 'Pläne konnten nicht geladen werden. Bitte aktualisieren Sie die Seite.',
      actionFailed: 'Planänderung fehlgeschlagen. Bitte versuchen Sie es erneut.',
      subscriptionSuccess: 'Abonnement erfolgreich aktiviert! Ihr Tracking ist jetzt aktiv.',
      planNowSuccess: 'Erledigt. Sie sind jetzt im Tarif {plan}.',
      scheduledSuccess: 'Bestätigt. Sie wechseln zu {plan} am Ende des bereits bezahlten Zeitraums.',
      scheduledBadge: 'Geplant',
      cancelConfirmationTrial: 'Sie befinden sich noch im kostenlosen Test, es wurde nichts berechnet. Bei einer Kündigung wechseln Sie sofort zurück zum Free-Tarif.',
      activationFailed: 'Wir konnten Ihr Abonnement nicht bestätigen. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.',
      planName: 'Plan',
      priceLabel: 'Preis',
      upgrade: 'Upgraden',
      downgrade: 'Herabstufen',
      historyDate: 'Datum',
      historyType: 'Typ',
      historyAmount: 'Betrag',
      historyStatus: 'Status',
      cancelTitle: 'Abonnement kündigen',
      confirmCancel: 'Ja, kündigen',
      keepPlan: 'Plan beibehalten',
      cancelConfirmation: 'Sind Sie sicher, dass Sie kündigen möchten? Ihr Plan bleibt bis zum Ende des aktuellen Abrechnungszeitraums aktiv.',
      featureLabels: {
        google_ads: 'Google Ads Enhanced Conversions',
        meta_capi: 'Meta Conversions API',
        identity_resolution: 'Identity Resolution',
        basic_support: 'Basis-Support',
        priority_support: 'Prioritäts-Support',
        dedicated_support: 'Dedizierter Support',
        advanced_analytics: 'Erweiterte Analysen',
        api_access: 'API-Zugang',
        ga4: 'Google Analytics 4 (Measurement Protocol)',
        tiktok: 'TikTok Events API',
        pinterest: 'Pinterest Conversions API',
        snapchat: 'Snapchat Conversions API',
        reconciliation: 'Conversion-Abgleich',
        live_debugger: 'Live-Event-Debugger',
        alerts: 'Tracking-Warnungen',
        export_csv: 'CSV-Export',
        webhooks_out: 'Ausgehende Webhooks',
        server_side_tracking: 'Server-side Tracking (umgeht Werbeblocker)',
        one_year_cookies: '1-Jahres-Server-Cookies (Safari-ITP-Umgehung)',
        click_id_capture: 'Click-ID-Erfassung (gclid, fbclid, ttclid usw.)',
        full_funnel: 'Full-Funnel-Übertragung (Aufrufe, Warenkörbe, Checkouts)',
        dedup: 'Deterministische Event-Deduplizierung',
        auto_backfill: 'Automatischer Nachversand nach einer Pause (Kontingent, deaktivierter Kanal)',
        consent_mode: 'Google Consent Mode v2',
        cmp_autodetect: 'CMP-Auto-Erkennung (6 Anbieter)',
        gdpr: 'DSGVO-konform',
        realtime_dashboard: 'Echtzeit-Dashboard',
        emq_monitoring: 'Meta-EMQ-Überwachung',
        all: 'Alle Funktionen',
      },
    },
    settings: {
      title: 'Einstellungen',
      subtitle: 'Konfigurieren Sie Ihre Shop-Einstellungen',
      shopInfo: 'Shop-Informationen',
      shopDomain: 'Domain',
      shopName: 'Name',
      shopId: 'Trackful-ID',
      createdAt: 'Erstellt am',
      consentSettings: 'Einwilligungseinstellungen',
      consentDesc: 'Konfigurieren Sie die Standard-Einwilligungseinstellungen für Consent Mode v2. Diese Werte werden verwendet, wenn der Besucher noch keine Einwilligung erteilt hat.',
      defaultConsent: 'Standard-Einwilligung',
      adStorage: 'Werbe-Speicher',
      adStorageHelp: 'Erlaubt das Speichern von Cookies für Werbung',
      analyticsStorage: 'Analytics-Speicher',
      analyticsStorageHelp: 'Erlaubt das Speichern von Cookies für Analysen',
      adUserData: 'Werbe-Nutzerdaten',
      adUserDataHelp: 'Erlaubt das Senden von Nutzerdaten für Werbung',
      adPersonalization: 'Werbe-Personalisierung',
      adPersonalizationHelp: 'Erlaubt die Personalisierung von Werbeanzeigen',
      granted: 'Erteilt',
      denied: 'Verweigert',
      unspecified: 'Nicht angegeben (empfohlen bei einem Consent-Banner)',
      saveConsent: 'Einwilligung speichern',
      saveFailed: 'Speichern fehlgeschlagen',
      language: 'Sprache',
      languageHelp: 'Sprache der Benutzeroberfläche auswählen',
      french: 'Französisch',
      english: 'Englisch',
      german: 'Deutsch',
      spanish: 'Spanisch',
      portugueseBR: 'Portugiesisch (Brasilien)',
      japanese: 'Japanisch',
      dutch: 'Niederländisch',
    },
    backfillHold: {
      titleOne: '{n} vergangene Bestellung kann an Ihre Werbeplattformen gesendet werden',
      titleMany: '{n} vergangene Bestellungen können an Ihre Werbeplattformen gesendet werden',
      action: 'An meine Werbeplattformen senden',
      actionPending: 'Wird gesendet...',
      imported: 'Bei der Installation von Trackful haben wir Ihre Bestellungen der letzten 90 Tage importiert. Sie sind bereits in diesem Dashboard und in Google Analytics 4 enthalten.',
      notSent: 'An Google Ads, Meta, TikTok, Pinterest und Snapchat haben wir sie nicht gesendet. Das sind Ihre Werbekonten, und wenn ein anderes Tracking-Tool dieselben Bestellungen bereits gemeldet hat, würde ein erneutes Senden Ihren Verlauf verdoppeln. Es wird nichts gesendet, bevor Sie es entscheiden.',
      windows: 'Nur aktuelle Bestellungen werden akzeptiert: Meta lehnt alles ab, was älter als 7 Tage ist, Google Ads etwa ab 90 Tagen. Ältere Bestellungen bleiben in jedem Fall in Ihrem Dashboard.',
      error: 'Beim Senden ist ein Fehler aufgetreten. Es ging nichts verloren, Sie können es erneut versuchen.',
      success: 'Gesendet. Sie erscheinen in den nächsten Minuten auf Ihren Werbeplattformen.',
    },
    competingPixels: {
      criticalTitle: 'Ihre Käufe werden bei Meta wahrscheinlich doppelt gezählt',
      criticalAction: 'Meta-Einstellungen öffnen',
      criticalDetected: 'Wir haben auf Ihrem Shop etwas erkannt, das nach {sources} aussieht. Es sendet eigene Kaufereignisse an Meta, und Trackful sendet dieselben Käufe serverseitig. Diese beiden Quellen lassen sich nicht deduplizieren: Der Verkaufskanal vergibt eigene Ereignis-IDs und legt sie nirgends offen, sodass Meta die beiden Ereignisse nicht unterscheiden kann.',
      criticalAdvice: 'Behalten Sie eine Quelle, nicht beide. Wir empfehlen, Trackful zu behalten und das Tracking des Verkaufskanals abzuschalten, denn Trackful meldet auch die Käufe, die ein Browser-Pixel durch Werbeblocker, iOS-Tracking-Beschränkungen und gehostete Checkouts verliert. Wenn Sie lieber den Verkaufskanal behalten, deaktivieren Sie stattdessen Meta in Trackful.',
      warningTitle: 'Ein Browser-Pixel läuft parallel zu Trackful',
      warningDetected: 'Wir haben {sources} auf Ihrem Shop erkannt. Dieses Pixel und Trackful erreichen dasselbe Werbekonto, derselbe Kauf kann also doppelt gezählt werden.',
      warningDedup: 'Diese Quellen konfigurieren Sie selbst, Deduplizierung ist also möglich: Senden Sie die Ereignis-ID von Trackful mit dem Browser-Ereignis. Bei einem Kauf ist unsere Ereignis-ID die {strong}.',
      warningDedupStrong: 'Bestellnummer',
      itemMeta: 'Meta Pixel: Setzen Sie {code} in Ihrem Browser-Purchase-Ereignis auf die Bestellnummer.',
      itemOthers: 'TikTok, Pinterest und Snap: Setzen Sie {code} auf die Bestellnummer.',
      itemGtm: 'Google Tag Manager: Geben Sie die Bestellnummer an die Anbieter-Tags weiter, die der Container auslöst.',
      selfDeduping: '{sources} können Sie so belassen: Google Ads dedupliziert selbst über die Bestellnummer.',
      lastDetected: 'Zuletzt auf Ihrem Shop erkannt: {date}',
    },
  },

  // ===========================================================================
  // ESPANOL (Spanish — neutral)
  // ===========================================================================
  es: {
    nav: {
      dashboard: 'Panel de control',
      googleAds: 'Google Ads',
      meta: 'Meta CAPI',
      billing: 'Facturación',
      settings: 'Configuración',
    },
    common: {
      save: 'Guardar',
      cancel: 'Cancelar',
      connect: 'Conectar',
      disconnect: 'Desconectar',
      enable: 'Activar',
      disable: 'Desactivar',
      test: 'Probar',
      refresh: 'Actualizar',
      loading: 'Cargando...',
      error: 'Error',
      success: 'Éxito',
      warning: 'Advertencia',
      connected: 'Conectado',
      notConnected: 'No conectado',
      active: 'Activo',
      disabled: 'Desactivado',
      pending: 'Pendiente',
      sent: 'Enviado',
      thisMonth: 'Este mes',
      selectAccount: 'Seleccionar cuenta',
      selectAction: 'Seleccionar acción',
      createNew: 'Crear nuevo',
      actionFailed: 'Algo salió mal. Por favor, inténtalo de nuevo.',
      configFailed: 'La configuración falló. Por favor, inténtalo de nuevo.',
      gateNeedsPlan: 'Esta función requiere el plan {plan}',
      gateNeedsSubscription: 'Active su suscripción para desbloquear esta función',
      gateBody: 'La captura de datos continúa con normalidad. Cambie al plan {plan} para activar esta función en unos clics.',
      gateCta: 'Ver planes',
      disableChannelTitle: '¿Desactivar el envío a {channel}?',
      disableChannelBody: 'La captura sigue funcionando al 100%: cada conversión se sigue registrando en Trackful. Solo se pausa el envío a {channel}.',
      disableChannelSafety: 'Al reactivarlo, todas las conversiones capturadas durante la pausa se reenviarán automáticamente a {channel}. Recibirá un email de confirmación.',
    },
    dashboard: {
      title: 'Panel de control',
      totalConversions: 'Conversiones totales',
      totalRevenue: 'Ingresos totales',
      googleAdsTracked: 'Google Ads rastreadas',
      deliveryRate: 'Enviadas a las plataformas',
      metaTracked: 'Meta rastreadas',
      ofConversions: 'de las conversiones',
      attributionRate: 'Tasa de atribución',
      conversionsWithAttribution: 'Conversiones con atribución de click ID',
      excellent: 'Excelente',
      good: 'Bueno',
      needsImprovement: 'Necesita mejora',
      integrationStatus: 'Estado de integraciones',
      eventMatchQuality: 'Calidad de coincidencia Meta',
      emqNotAvailable: 'Datos EMQ no disponibles. Conecte Meta CAPI y envíe eventos.',
      recentConversions: 'Conversiones recientes',
      deliveryPerChannel: 'Entrega por canal: cada conversión se envía a todos los canales activos; cada plataforma realiza después su propia atribución.',
      noConversions: 'Sin conversiones',
      noConversionsDesc: 'Las conversiones aparecerán aquí una vez que reciba pedidos con el seguimiento activado.',
      order: 'Pedido',
      revenue: 'Ingresos',
      googleAds: 'Google Ads',
      meta: 'Meta',
      attribution: 'Atribución',
      date: 'Fecha',
      warningNotConnected: 'Ni Google Ads ni Meta CAPI están conectados. Configure sus integraciones para iniciar el seguimiento.',
      warningGoogleNotConnected: 'Google Ads no está conectado. Configúrelo para rastrear conversiones.',
      warningMetaNotConnected: 'Meta CAPI no está conectado. Configúrelo para rastrear conversiones.',
      integrationLoadError: 'No se pudo cargar el estado de las integraciones. Actualice la página.',
      failedThisMonth: 'Fallos este mes',
      yes: 'Si',
      no: 'No',
      direct: 'Directo',
    },
    setup: {
      activeTitle: 'Tu seguimiento server-side empresarial está activo',
      activeBadge: 'Activo',
      activeBody: 'Nada más que hacer. Nos encargamos del resto: captura de eventos, cifrado de datos personales, envío a las plataformas publicitarias y autorreparación ante errores.',
      activeLiveChannels: 'Canales activos',
      inactiveTitle: 'Activa tu seguimiento server-side',
      inactiveBody: 'Conecta al menos una plataforma publicitaria. Un solo canal basta para empezar: Trackful configura automáticamente todo lo necesario entre bastidores (acción de conversión, píxel, atribución).',
      connect: 'Conectar',
      connected: 'Conectado',
      needsAction: 'Finalizar',
      connectFailed: 'La conexión falló. Inténtalo de nuevo.',
      pixelLive: 'Píxel activo, eventos capturados',
      pixelWaiting: 'Esperando los primeros eventos del píxel',
      clickTrackerTitle: 'Active el seguimiento de clics en su tema',
      clickTrackerActive: 'Activo',
      clickTrackerCta: 'Activar ahora',
      clickTrackerBody: 'El rastreador de clics captura los identificadores publicitarios (gclid, fbclid...) y establece cookies de servidor de 1 año: el requisito para una atribución precisa. Shopify no puede activarlo automáticamente: un clic en Activar y luego Guardar en el editor de temas.',
      clickTrackerHint: 'Después de guardar, abra su tienda una vez: este paso se pondrá verde automáticamente.',
      sendTestEvent: 'Enviar una conversión de prueba',
      testEventOk: 'Conversión de prueba aceptada por Google ✓',
      testEventFail: 'La prueba falló: abra la página de Google Ads para más detalles.',
      consentNotice: 'Cumplimiento: sin un banner de cookies (Consent Mode v2), las conversiones de la UE no pueden llevar datos de clientes a Meta y GA4. Configure su CMP.',
      advanced: 'Opciones avanzadas',
      funnelTitle: 'Reenvío del embudo completo',
      funnelBody: 'Envía todo el recorrido de compra (productos vistos, añadidos al carrito, checkouts) a tus plataformas publicitarias como señales de optimización adicionales, no solo las compras. Las compras siempre se envían.',
      funnelOn: 'Activado',
      funnelOff: 'Desactivado',
      funnelChannelsTitle: 'Lo que recibe cada plataforma',
      funnelChannelsBody: 'Las compras siempre se envían. El resto del recorrido (vistas de producto, añadidos al carrito, checkouts) solo se envía cuando podemos garantizar que no se contará dos veces.',
      funnelSends: 'Recorrido completo',
      funnelPurchasesOnly: 'Solo compras',
      funnelWhyOwn: 'Nuestras propias acciones de conversión: nada de lo suyo puede contarse dos veces.',
      funnelWhyStamped: 'Su etiqueta ({sources}) ahora lleva nuestro id de evento, así que la plataforma cuenta uno, no dos.',
      funnelWhyEmpty: 'La propiedad no recibía nada antes de nosotros.',
      funnelWhyDeclared: 'Nos dijo que Trackful es su única fuente aquí.',
      funnelWhyCollision: 'Otra fuente ya alimenta esta plataforma ({sources}). Enviamos solo compras para no duplicar sus cifras.',
      funnelAsk: '¿Otra etiqueta ya envía eventos a esta plataforma desde el navegador? Los canales de venta nativos de Shopify cuentan.',
      funnelAskKeep: 'Sí, la mantengo',
      funnelAskOnly: 'No, Trackful es mi única fuente',
      funnelAskWhy: 'No podemos verlo por nosotros mismos: los píxeles de los canales nativos de Shopify se ejecutan en un sandbox aislado.',
      pausedTitle: 'Envío en pausa (tus datos están a salvo)',
      pausedCta: 'Mejorar plan',
      pausedBody: 'Has alcanzado el límite de tu plan. Seguimos capturando todo, pero el envío a tus plataformas publicitarias está en pausa. Mejora tu plan para reanudarlo: reenviaremos automáticamente todo lo capturado.',
      pausedBodyCount: 'Límite del plan alcanzado. {count} conversión(es) capturada(s) en espera de envío. Seguimos capturando todo; mejora tu plan para reanudar el envío y lo reenviaremos todo automáticamente.',
    },
    googleAds: {
      title: 'Google Ads',
      subtitle: 'Conecte Google Ads para el seguimiento con Enhanced Conversions',
      connectionStatus: 'Estado de conexión',
      account: 'Cuenta',
      conversionAction: 'Acción de conversión',
      lastSync: 'Última sincronización',
      testConnection: 'Probar conexión',
      connectGoogleAds: 'Conectar Google Ads',
      connectDescription: 'Conecte su cuenta de Google Ads para iniciar el seguimiento de conversiones con Enhanced Conversions.',
      enhancedConversions: 'Enhanced Conversions',
      enhancedConversionsDesc: 'Enhanced Conversions mejora la precisión del seguimiento enviando de forma segura datos hasheados del cliente (email, teléfono, dirección) a Google Ads.',
      enableEnhancedConversions: 'Activar Enhanced Conversions',
      enhancedConversionsHelp: 'Requiere consentimiento del cliente para ad_user_data según RGPD/Consent Mode v2',
      ecActionRequiredTitle: 'Acción requerida en Google Ads',
      ecActionRequiredIntro: 'Enhanced Conversions no se puede activar desde esta aplicación. Debe configurarlo en Google Ads (Objetivos > Conversiones > Configuración):',
      ecStep1: '1. Active "Enhanced conversions for web"',
      ecStep2: '2. Acepte las condiciones sobre datos de clientes',
      ecStep3: '3. Elija "API de Google Ads" como método',
      ecNote: 'Hasta que se completen estos pasos, los datos de cliente cifrados que enviamos no son procesados por Google.',
      ecReady: 'Enhanced Conversions está listo: las condiciones sobre datos de clientes están aceptadas en esta cuenta de Google Ads.',
      conversionActionsTitle: 'Acciones de conversión',
      conversionActionsDesc: 'Trackful envía la compra (acción principal) y también puede enviar los pasos del embudo como acciones secundarias.',
      autoCreatedNote: 'Creadas automáticamente en su cuenta de Google Ads al seleccionar la cuenta. Encuéntrelas en Objetivos > Conversiones.',
      funnelOffHint: 'El reenvío del embudo completo está desactivado (Panel > Opciones avanzadas): estos interruptores se aplican a los eventos secundarios cuando esté activado. Las compras siempre se envían.',
      disableTitle: '¿Desactivar el envío a Google Ads?',
      disableConfirmBody: 'La captura sigue funcionando al 100%: cada conversión se sigue registrando en Trackful. Solo se pausa el envío a Google Ads.',
      disableConfirmSafety: 'Al reactivarlo, todas las conversiones capturadas durante la pausa se reenviarán automáticamente a Google Ads. Recibirá un email de confirmación.',
      funnelPurchase: 'Compra',
      funnelPurchaseDesc: 'El evento más importante: la venta. Siempre se envía.',
      primaryBadge: 'Principal',
      createPrimaryAction: 'Crear acción de compra',
      activateAction: 'Activar',
      funnelProductView: 'Vista de producto',
      funnelProductViewDesc: 'Cuando un visitante ve una página de producto.',
      funnelAddToCart: 'Añadir al carrito',
      funnelAddToCartDesc: 'Cuando se añade un producto al carrito. Fuerte señal de intención.',
      funnelBeginCheckout: 'Inicio de pago',
      funnelBeginCheckoutDesc: 'Cuando un visitante inicia el pago.',
      funnelAddPaymentInfo: 'Añadir datos de pago',
      funnelAddPaymentInfoDesc: 'Cuando se introducen los datos de pago.',
      setupInstructions: 'Instrucciones de configuración',
      step1: '1. Haga clic en "Conectar Google Ads" para autorizar el acceso',
      step2: '2. Seleccione la cuenta de Google Ads a utilizar',
      step3: '3. Elija o cree una acción de conversión para compras',
      step4: '4. Active Enhanced Conversions para mayor precisión',
      selectAccountTitle: 'Seleccionar cuenta de Google Ads',
      availableAccounts: 'Cuentas disponibles',
      noAccounts: 'No se encontraron cuentas. Asegúrese de que su cuenta de Google tenga acceso a al menos una cuenta de Google Ads.',
      selectAccountPrompt: 'Aún no hay ninguna cuenta seleccionada. Elige la cuenta de Google Ads que quieres registrar.',
      selectConversionTitle: 'Seleccionar acción de conversión',
      selectConversionDesc: 'Seleccione una acción de conversión existente o cree una nueva para rastrear compras.',
      availableActions: 'Acciones de conversión disponibles',
      noActions: 'No se encontraron acciones de conversión. Haga clic en "Crear nuevo" para crear una.',
      tagBasedAction: 'Acción basada en etiqueta: no puede recibir envíos del lado del servidor. Elija una acción de Trackful o cree una.',
      changeAction: 'Cambiar acción',
      conflictTitle: 'Riesgo de doble contabilización detectado',
      conflictDesc: 'Estas otras acciones de conversión de Compra también están definidas como Principales en su cuenta de Google Ads. Mientras haya varias acciones de compra principales, sus compras se cuentan dos veces en la columna Conversiones y en las pujas:',
      conflictFix: 'Hacer de Trackful la única acción principal',
      conflictKeep: 'Mantener como está',
      conflictConfirmDesc: 'Las acciones siguientes pasarán a Secundarias (observación) en su cuenta de Google Ads. Seguirán registrándose pero ya no contarán en la columna Conversiones ni en las pujas. Reversible en cualquier momento.',
      conflictFixed: 'Listo. Las siguientes acciones son ahora solo de observación:',
      conflictUndo: 'Deshacer',
      disconnectTitle: 'Desconectar Google Ads',
      disconnectConfirm: '¿Está seguro de que desea desconectar Google Ads? El seguimiento de conversiones se detendrá hasta que se reconecte.',
      disconnectRemoveActions: 'Eliminar también las acciones de conversión creadas por Trackful en Google Ads',
      disconnectRemoveActionsHelp: 'Pasarán al estado «Eliminada»: dejan de registrar, pero sus datos históricos permanecen en los informes.',
      testSuccess: '¡Prueba exitosa! La configuración es válida.',
      testRequestId: 'ID de solicitud de Google',
      testFailed: 'La prueba falló',
      enableTracking: 'Activar seguimiento',
      disableTracking: 'Desactivar seguimiento',
      actionFailed: 'Se produjo un error. Inténtelo de nuevo.',
    },
    meta: {
      title: 'Meta CAPI',
      subtitle: 'Configure Meta Conversions API para el seguimiento del lado del servidor',
      connectionStatus: 'Estado de conexión',
      pixelId: 'ID de Pixel',
      testEventCode: 'Código de evento de prueba',
      lastSync: 'Última sincronización',
      sendTestEvent: 'Enviar evento de prueba',
      updateConfiguration: 'Modificar configuración',
      configureMeta: 'Configurar Meta CAPI',
      connectDescription: 'Conecte su Meta Pixel para iniciar el seguimiento de conversiones a través de la API de Conversiones.',
      emq: 'Calidad de coincidencia de eventos (EMQ)',
      emqDesc: 'El EMQ mide qué tan bien sus datos de eventos coinciden con los usuarios de Meta. Puntuaciones más altas significan mejor atribución y rendimiento publicitario.',
      overallScore: 'Puntuación general',
      recommendations: 'Recomendaciones',
      emqNotAvailable: 'Datos EMQ no disponibles. Envíe eventos y actualice para ver las puntuaciones.',
      setupInstructions: 'Instrucciones de configuración',
      step1: '1. Vaya a su Administrador de eventos de Meta',
      step2: '2. Encuentre su ID de Pixel (Configuración > ID de Pixel)',
      step3: '3. Genere un Token de acceso (Configuración > API de Conversiones > Generar Token)',
      step4: '4. Opcionalmente, cree un Código de evento de prueba para depuración',
      step5: '5. Ingrese estos valores en el formulario de configuración',
      configureTitle: 'Configurar Meta CAPI',
      saveConfiguration: 'Guardar configuración',
      pixelIdPlaceholder: '1234567890123456',
      pixelIdHelp: 'Su ID de Meta Pixel del Administrador de eventos',
      accessToken: 'Token de acceso',
      accessTokenPlaceholder: 'EAAx...',
      accessTokenHelp: 'Token de acceso de la API de Conversiones (manténgalo en secreto)',
      testEventCodeOptional: 'Código de evento de prueba (Opcional)',
      testEventCodePlaceholder: 'TEST12345',
      testEventCodeHelp: 'Use esto para enviar eventos a Test Events en el Administrador de eventos',
      disconnectTitle: 'Desconectar Meta CAPI',
      disconnectConfirm: '¿Está seguro de que desea desconectar Meta CAPI? El seguimiento de conversiones se detendrá hasta que se reconecte.',
      testSuccess: '¡Evento de prueba enviado con éxito! Eventos recibidos:',
      testFailed: 'La prueba falló',
      required: 'El ID de Pixel y el Token de acceso son obligatorios',
      enableTracking: 'Activar seguimiento',
      disableTracking: 'Desactivar seguimiento',
      actionFailed: 'Se produjo un error. Inténtelo de nuevo.',
    },
    billing: {
      title: 'Facturación',
      subtitle: 'Gestione su suscripción y supervise su uso',
      currentPlan: 'Plan actual',
      usage: 'Uso',
      ordersUsed: 'Pedidos rastreados',
      ordersRemaining: 'Eventos restantes',
      unlimited: 'Ilimitado',
      ofLimit: 'del límite',
      trial: 'Prueba gratuita',
      trialActive: 'Prueba activa',
      trialEndsIn: 'La prueba termina en',
      trialDaysRemaining: 'restantes',
      days: 'días',
      upgradeNow: 'Mejorar ahora',
      alreadyCancelled: 'Cancelado. El acceso continúa hasta el final del periodo.',
      upgradePlan: 'Mejorar plan',
      downgradePlan: 'Reducir plan',
      cancelSubscription: 'Cancelar suscripción',
      scheduledChange: 'Cambio a {plan} el {date}. Hasta entonces conserva {current}.',
      // Billing cadence. `savePercent` and `billedYearly` are templates:
      // the numbers come from the API so the page can never quote a
      // discount the charge does not match.
      billMonthly: 'Mensual',
      billAnnually: 'Anual',
      savePercent: 'Ahorra un {percent} %',
      billedYearly: 'se factura {amount} al año',
      switchToYearly: 'Cambiar a anual',
      switchToMonthly: 'Cambiar a mensual',
      selectPlan: 'Seleccionar este plan',
      currentPlanBadge: 'Plan actual',
      month: '/mes',
      ordersPerMonth: 'pedidos rastreados/mes',
      subscribe: 'Suscribirse',
      havePartnerCode: '¿Tiene un código de socio?',
      partnerCodeApplied: 'Código de socio aplicado',
      partnerCodeAppliedHelp: 'Su descuento se aplica antes de confirmar, por lo que el precio que le muestra Shopify ya lo incluye.',
      partnerCodePrompt: 'Si un socio le dio un código, introdúzcalo antes de suscribirse.',
      partnerCodeFirstOnly: 'Un código de socio se introduce antes de su primera suscripción. Un descuento en curso le acompaña al cambiar de plan, siempre que mantenga la misma periodicidad.',
      partnerCodePlaceholder: 'Opcional',
      partnerCodeLabel: 'Código de socio',
      partnerCodeHelp: 'Se aplica a su primer mes. Un código desconocido simplemente significa que no hay descuento.',
      sectionChannels: 'Canales publicitarios',
      sectionTracking: 'Tracking y atribución',
      sectionCompliance: 'Cumplimiento y consentimiento',
      sectionInsights: 'Datos e informes',
      sectionSupport: 'Soporte',
      features: 'Características',
      plans: 'Planes disponibles',
      starter: 'Starter',
      growth: 'Growth',
      pro: 'Pro',
      warningNearLimit: 'Se está acercando a su límite mensual de eventos.',
      warningOverLimit: 'Ha superado su límite de eventos. Mejore su plan para continuar el seguimiento.',
      warningTrialEnding: 'Su prueba está por terminar. Suscríbase para continuar.',
      billingHistory: 'Historial de facturación',
      noHistory: 'Sin historial de facturación',
      recommendation: 'Recomendación',
      upgradeToRecommended: 'Mejorar a {plan}',
      plansLoadError: 'No se pudieron cargar los planes. Actualice la página.',
      actionFailed: 'Error al cambiar de plan. Inténtelo de nuevo.',
      subscriptionSuccess: '¡Suscripción activada con éxito! El seguimiento está ahora activo.',
      planNowSuccess: 'Listo. Ahora estás en el plan {plan}.',
      scheduledSuccess: 'Confirmado. Pasarás a {plan} al final del período que ya has pagado.',
      scheduledBadge: 'Programado',
      cancelConfirmationTrial: 'Todavía estás en la prueba gratuita, no se te ha cobrado nada. Al cancelar vuelves al plan Free de inmediato.',
      activationFailed: 'No pudimos confirmar su suscripción. Inténtelo de nuevo o contacte con soporte.',
      planName: 'Plan',
      priceLabel: 'Precio',
      upgrade: 'Actualizar',
      downgrade: 'Rebajar',
      historyDate: 'Fecha',
      historyType: 'Tipo',
      historyAmount: 'Importe',
      historyStatus: 'Estado',
      cancelTitle: 'Cancelar suscripción',
      confirmCancel: 'Sí, cancelar',
      keepPlan: 'Mantener mi plan',
      cancelConfirmation: '¿Estás seguro de querer cancelar? Tu plan seguirá activo hasta el final del período de facturación actual.',
      featureLabels: {
        google_ads: 'Google Ads Enhanced Conversions',
        meta_capi: 'Meta Conversions API',
        identity_resolution: 'Resolución de identidad',
        basic_support: 'Soporte básico',
        priority_support: 'Soporte prioritario',
        dedicated_support: 'Soporte dedicado',
        advanced_analytics: 'Analíticas avanzadas',
        api_access: 'Acceso API',
        ga4: 'Google Analytics 4 (Measurement Protocol)',
        tiktok: 'TikTok Events API',
        pinterest: 'Pinterest Conversions API',
        snapchat: 'Snapchat Conversions API',
        reconciliation: 'Reconciliación de conversiones',
        live_debugger: 'Depurador de eventos en tiempo real',
        alerts: 'Alertas de tracking',
        export_csv: 'Exportación CSV',
        webhooks_out: 'Webhooks salientes',
        server_side_tracking: 'Tracking server-side (evita los bloqueadores de anuncios)',
        one_year_cookies: 'Cookies de servidor de 1 año (evita Safari ITP)',
        click_id_capture: 'Captura de click IDs (gclid, fbclid, ttclid, etc.)',
        full_funnel: 'Envío del embudo completo (vistas, carritos, checkouts)',
        dedup: 'Deduplicación determinista de eventos',
        auto_backfill: 'Reenvío automático tras una pausa (cuota, canal desactivado)',
        consent_mode: 'Google Consent Mode v2',
        cmp_autodetect: 'Detección automática de CMP (6 proveedores)',
        gdpr: 'Conforme con el RGPD',
        realtime_dashboard: 'Panel en tiempo real',
        emq_monitoring: 'Monitoreo EMQ de Meta',
        all: 'Todas las características',
      },
    },
    settings: {
      title: 'Configuración',
      subtitle: 'Configure los ajustes de su tienda',
      shopInfo: 'Información de la tienda',
      shopDomain: 'Dominio',
      shopName: 'Nombre',
      shopId: 'ID Trackful',
      createdAt: 'Creado el',
      consentSettings: 'Ajustes de consentimiento',
      consentDesc: 'Configure los ajustes de consentimiento predeterminados para Consent Mode v2. Estos valores se usan cuando el visitante aún no ha dado su consentimiento.',
      defaultConsent: 'Consentimiento predeterminado',
      adStorage: 'Almacenamiento publicitario',
      adStorageHelp: 'Permite almacenar cookies para publicidad',
      analyticsStorage: 'Almacenamiento de analytics',
      analyticsStorageHelp: 'Permite almacenar cookies para análisis',
      adUserData: 'Datos de usuario pub.',
      adUserDataHelp: 'Permite enviar datos de usuario para publicidad',
      adPersonalization: 'Personalización pub.',
      adPersonalizationHelp: 'Permite la personalización de anuncios',
      granted: 'Concedido',
      denied: 'Denegado',
      unspecified: 'Sin especificar (recomendado si usa un banner de consentimiento)',
      saveConsent: 'Guardar consentimiento',
      saveFailed: 'Error al guardar',
      language: 'Idioma',
      languageHelp: 'Seleccione el idioma de la interfaz',
      french: 'Francés',
      english: 'Inglés',
      german: 'Alemán',
      spanish: 'Español',
      portugueseBR: 'Portugués (Brasil)',
      japanese: 'Japonés',
      dutch: 'Neerlandés',
    },
    backfillHold: {
      titleOne: '{n} pedido anterior está listo para enviarse a tus plataformas publicitarias',
      titleMany: '{n} pedidos anteriores están listos para enviarse a tus plataformas publicitarias',
      action: 'Enviar a mis plataformas',
      actionPending: 'Enviando...',
      imported: 'Importamos tus pedidos de los últimos 90 días al instalar Trackful. Ya están contabilizados en este panel y en Google Analytics 4.',
      notSent: 'No los hemos enviado a Google Ads, Meta, TikTok, Pinterest ni Snapchat. Son tus cuentas publicitarias, y si otra herramienta de seguimiento ya reportó esos mismos pedidos, volver a enviarlos duplicaría tu historial. No se envía nada hasta que tú lo decidas.',
      windows: 'Solo se aceptarán los pedidos recientes: Meta rechaza todo lo que supere los 7 días, y Google Ads unos 90. Los más antiguos permanecen en tu panel de todas formas.',
      error: 'Hubo un problema al enviarlos. No se perdió nada, puedes intentarlo de nuevo.',
      success: 'Enviados. Aparecerán en tus plataformas en los próximos minutos.',
    },
    competingPixels: {
      criticalTitle: 'Es probable que tus compras se cuenten dos veces en Meta',
      criticalAction: 'Abrir la configuración de Meta',
      criticalDetected: 'Detectamos algo que parece {sources} en tu tienda. Envía sus propios eventos de compra a Meta, y Trackful envía esas mismas compras desde el servidor. Estas dos fuentes no se pueden deduplicar: el canal de venta elige sus propios identificadores de evento y no los expone, así que Meta no puede distinguir uno del otro.',
      criticalAdvice: 'Conserva una sola fuente, no las dos. Recomendamos mantener Trackful y desactivar el seguimiento del canal de venta, porque Trackful también reporta las compras que un píxel de navegador pierde por los bloqueadores de anuncios, las restricciones de iOS y los pagos alojados. Si prefieres conservar el canal de venta, desactiva Meta en Trackful.',
      warningTitle: 'Un píxel de navegador funciona junto a Trackful',
      warningDetected: 'Detectamos {sources} en tu tienda. Ese píxel y Trackful llegan a la misma cuenta publicitaria, así que una misma compra puede contarse dos veces.',
      warningDedup: 'Estas fuentes las configuras tú, así que la deduplicación es posible: envía el identificador de evento de Trackful junto con el evento del navegador. En una compra, nuestro identificador es el {strong}.',
      warningDedupStrong: 'número de pedido',
      itemMeta: 'Píxel de Meta: asigna {code} al número de pedido en tu evento Purchase del navegador.',
      itemOthers: 'TikTok, Pinterest y Snap: asigna {code} al número de pedido.',
      itemGtm: 'Google Tag Manager: pasa el número de pedido a las etiquetas que dispare el contenedor.',
      selfDeduping: '{sources} puede quedarse tal cual: Google Ads deduplica por sí solo con el número de pedido.',
      lastDetected: 'Última detección en tu tienda: {date}',
    },
  },

  // ===========================================================================
  // PORTUGUES BRASILEIRO (Brazilian Portuguese)
  // ===========================================================================
  'pt-BR': {
    nav: {
      dashboard: 'Painel',
      googleAds: 'Google Ads',
      meta: 'Meta CAPI',
      billing: 'Faturamento',
      settings: 'Configurações',
    },
    common: {
      save: 'Salvar',
      cancel: 'Cancelar',
      connect: 'Conectar',
      disconnect: 'Desconectar',
      enable: 'Ativar',
      disable: 'Desativar',
      test: 'Testar',
      refresh: 'Atualizar',
      loading: 'Carregando...',
      error: 'Erro',
      success: 'Sucesso',
      warning: 'Atenção',
      connected: 'Conectado',
      notConnected: 'Não conectado',
      active: 'Ativo',
      disabled: 'Desativado',
      pending: 'Pendente',
      sent: 'Enviado',
      thisMonth: 'Este mês',
      selectAccount: 'Selecionar conta',
      selectAction: 'Selecionar ação',
      createNew: 'Criar novo',
      actionFailed: 'Algo deu errado. Por favor, tente novamente.',
      configFailed: 'Falha na configuração. Por favor, tente novamente.',
      gateNeedsPlan: 'Este recurso requer o plano {plan}',
      gateNeedsSubscription: 'Ative sua assinatura para desbloquear este recurso',
      gateBody: 'A captura de dados continua normalmente. Mude para o plano {plan} para ativar este recurso em poucos cliques.',
      gateCta: 'Ver planos',
      disableChannelTitle: 'Desativar o envio para {channel}?',
      disableChannelBody: 'A captura continua a 100%: cada conversão continua registrada no Trackful. Apenas o envio para {channel} é pausado.',
      disableChannelSafety: 'Ao reativar, todas as conversões capturadas durante a pausa serão reenviadas automaticamente para {channel}. Você receberá um email de confirmação.',
    },
    dashboard: {
      title: 'Painel',
      totalConversions: 'Conversões totais',
      totalRevenue: 'Receita total',
      googleAdsTracked: 'Google Ads rastreadas',
      deliveryRate: 'Enviadas às plataformas',
      metaTracked: 'Meta rastreadas',
      ofConversions: 'das conversões',
      attributionRate: 'Taxa de atribuição',
      conversionsWithAttribution: 'Conversões com atribuição de click ID',
      excellent: 'Excelente',
      good: 'Bom',
      needsImprovement: 'Precisa melhorar',
      integrationStatus: 'Status das integrações',
      eventMatchQuality: 'Qualidade de correspondência Meta',
      emqNotAvailable: 'Dados EMQ não disponíveis. Conecte o Meta CAPI e envie eventos.',
      recentConversions: 'Conversões recentes',
      deliveryPerChannel: 'Entrega por canal: cada conversão é enviada a todos os canais ativos; cada plataforma faz depois sua própria atribuição.',
      noConversions: 'Nenhuma conversão',
      noConversionsDesc: 'As conversões aparecerão aqui quando você receber pedidos com o rastreamento ativado.',
      order: 'Pedido',
      revenue: 'Receita',
      googleAds: 'Google Ads',
      meta: 'Meta',
      attribution: 'Atribuição',
      date: 'Data',
      warningNotConnected: 'Nem Google Ads nem Meta CAPI estão conectados. Configure suas integrações para iniciar o rastreamento.',
      warningGoogleNotConnected: 'Google Ads não está conectado. Configure-o para rastrear conversões.',
      warningMetaNotConnected: 'Meta CAPI não está conectado. Configure-o para rastrear conversões.',
      integrationLoadError: 'Não foi possível carregar o status das integrações. Atualize a página.',
      failedThisMonth: 'Falhas este mes',
      yes: 'Sim',
      no: 'Nao',
      direct: 'Direto',
    },
    setup: {
      activeTitle: 'O seu rastreamento server-side empresarial está ativo',
      activeBadge: 'Ativo',
      activeBody: 'Nada mais a fazer. Cuidamos do resto: captura de eventos, hash de dados pessoais, envio às plataformas de anúncios e autorrecuperação em caso de erro.',
      activeLiveChannels: 'Canais ativos',
      inactiveTitle: 'Ative o seu rastreamento server-side',
      inactiveBody: 'Conecte pelo menos uma plataforma de anúncios. Um único canal basta para começar: o Trackful configura automaticamente toda a parte técnica nos bastidores (ação de conversão, pixel, atribuição).',
      connect: 'Conectar',
      connected: 'Conectado',
      needsAction: 'Concluir',
      connectFailed: 'A conexão falhou. Tente novamente.',
      pixelLive: 'Pixel ativo, eventos capturados',
      pixelWaiting: 'Aguardando os primeiros eventos do pixel',
      clickTrackerTitle: 'Ative o rastreamento de cliques no seu tema',
      clickTrackerActive: 'Ativo',
      clickTrackerCta: 'Ativar agora',
      clickTrackerBody: 'O rastreador de cliques captura identificadores de anúncios (gclid, fbclid...) e define cookies de servidor de 1 ano: o pré-requisito para atribuição precisa. A Shopify não pode ativá-lo automaticamente: um clique em Ativar e depois Salvar no editor de tema.',
      clickTrackerHint: 'Depois de salvar, abra sua loja uma vez: esta etapa ficará verde automaticamente.',
      sendTestEvent: 'Enviar uma conversão de teste',
      testEventOk: 'Conversão de teste aceita pelo Google ✓',
      testEventFail: 'O teste falhou: abra a página do Google Ads para detalhes.',
      consentNotice: 'Conformidade: sem um banner de cookies (Consent Mode v2), as conversões da UE não podem levar dados de clientes para Meta e GA4. Configure seu CMP.',
      advanced: 'Opções avançadas',
      funnelTitle: 'Encaminhamento do funil completo',
      funnelBody: 'Envia toda a jornada de compra (produtos vistos, adições ao carrinho, checkouts) às suas plataformas de anúncios como sinais extras de otimização, não apenas as compras. As compras são sempre enviadas.',
      funnelOn: 'Ativado',
      funnelOff: 'Desativado',
      funnelChannelsTitle: 'O que cada plataforma recebe',
      funnelChannelsBody: 'As compras sempre são enviadas. O resto da jornada (visualizações de produto, adições ao carrinho, checkouts) só é enviado quando podemos garantir que não será contado duas vezes.',
      funnelSends: 'Jornada completa',
      funnelPurchasesOnly: 'Somente compras',
      funnelWhyOwn: 'Nossas próprias ações de conversão: nada do que é seu pode ser contado duas vezes.',
      funnelWhyStamped: 'Sua tag ({sources}) agora carrega nosso id de evento, então a plataforma conta um evento, não dois.',
      funnelWhyEmpty: 'A propriedade não recebia nada antes de nós.',
      funnelWhyDeclared: 'Você nos disse que o Trackful é sua única fonte aqui.',
      funnelWhyCollision: 'Outra fonte já alimenta esta plataforma ({sources}). Enviamos apenas compras para não dobrar seus números.',
      funnelAsk: 'Outra tag já envia eventos para esta plataforma pelo navegador? Os canais de venda nativos da Shopify contam.',
      funnelAskKeep: 'Sim, eu mantenho',
      funnelAskOnly: 'Não, o Trackful é minha única fonte',
      funnelAskWhy: 'Não conseguimos ver isso sozinhos: os pixels dos canais nativos da Shopify rodam em um sandbox isolado.',
      pausedTitle: 'Envio pausado (seus dados estão seguros)',
      pausedCta: 'Fazer upgrade',
      pausedBody: 'Você atingiu o limite do seu plano. Continuamos capturando tudo, mas o envio às suas plataformas de anúncios está pausado. Faça upgrade para retomar: reenviaremos automaticamente tudo o que foi capturado.',
      pausedBodyCount: 'Limite do plano atingido. {count} conversão(ões) capturada(s) aguardando envio. Continuamos capturando tudo; faça upgrade para retomar o envio e reenviaremos tudo automaticamente.',
    },
    googleAds: {
      title: 'Google Ads',
      subtitle: 'Conecte o Google Ads para rastreamento com Enhanced Conversions',
      connectionStatus: 'Status da conexão',
      account: 'Conta',
      conversionAction: 'Ação de conversão',
      lastSync: 'Última sincronização',
      testConnection: 'Testar conexão',
      connectGoogleAds: 'Conectar Google Ads',
      connectDescription: 'Conecte sua conta do Google Ads para iniciar o rastreamento de conversões com Enhanced Conversions.',
      enhancedConversions: 'Enhanced Conversions',
      enhancedConversionsDesc: 'O Enhanced Conversions melhora a precisão do rastreamento enviando com segurança dados hasheados do cliente (email, telefone, endereço) para o Google Ads.',
      enableEnhancedConversions: 'Ativar Enhanced Conversions',
      enhancedConversionsHelp: 'Requer consentimento do cliente para ad_user_data conforme LGPD/Consent Mode v2',
      ecActionRequiredTitle: 'Ação necessária no Google Ads',
      ecActionRequiredIntro: 'O Enhanced Conversions não pode ser ativado por este aplicativo. Você precisa configurá-lo no Google Ads (Metas > Conversões > Configurações):',
      ecStep1: '1. Ative "Enhanced conversions for web"',
      ecStep2: '2. Aceite os termos de dados do cliente',
      ecStep3: '3. Escolha "API do Google Ads" como método',
      ecNote: 'Até que essas etapas sejam concluídas, os dados de cliente com hash que enviamos não são processados pelo Google.',
      ecReady: 'O Enhanced Conversions está pronto: os termos de dados do cliente foram aceitos nesta conta do Google Ads.',
      conversionActionsTitle: 'Ações de conversão',
      conversionActionsDesc: 'O Trackful envia a compra (ação principal) e também pode enviar as etapas do funil como ações secundárias.',
      autoCreatedNote: 'Criadas automaticamente na sua conta do Google Ads quando você selecionou a conta. Encontre-as em Metas > Conversões.',
      funnelOffHint: 'O encaminhamento do funil completo está desativado (Painel > Opções avançadas): estes botões se aplicam aos eventos secundários quando ele estiver ativado. As compras são sempre enviadas.',
      disableTitle: 'Desativar o envio para o Google Ads?',
      disableConfirmBody: 'A captura continua a 100%: cada conversão continua registrada no Trackful. Apenas o envio para o Google Ads é pausado.',
      disableConfirmSafety: 'Ao reativar, todas as conversões capturadas durante a pausa serão reenviadas automaticamente ao Google Ads. Você receberá um email de confirmação.',
      funnelPurchase: 'Compra',
      funnelPurchaseDesc: 'O evento mais importante: a venda. Sempre enviado.',
      primaryBadge: 'Principal',
      createPrimaryAction: 'Criar ação de compra',
      activateAction: 'Ativar',
      funnelProductView: 'Visualização de produto',
      funnelProductViewDesc: 'Quando um visitante vê uma página de produto.',
      funnelAddToCart: 'Adicionar ao carrinho',
      funnelAddToCartDesc: 'Quando um produto é adicionado ao carrinho. Forte sinal de intenção.',
      funnelBeginCheckout: 'Início de checkout',
      funnelBeginCheckoutDesc: 'Quando um visitante inicia o checkout.',
      funnelAddPaymentInfo: 'Adicionar dados de pagamento',
      funnelAddPaymentInfoDesc: 'Quando os dados de pagamento são inseridos.',
      setupInstructions: 'Instruções de configuração',
      step1: '1. Clique em "Conectar Google Ads" para autorizar o acesso',
      step2: '2. Selecione a conta do Google Ads a ser usada',
      step3: '3. Escolha ou crie uma ação de conversão para compras',
      step4: '4. Ative o Enhanced Conversions para melhor precisão',
      selectAccountTitle: 'Selecionar conta do Google Ads',
      availableAccounts: 'Contas disponíveis',
      noAccounts: 'Nenhuma conta encontrada. Certifique-se de que sua conta Google tenha acesso a pelo menos uma conta do Google Ads.',
      selectAccountPrompt: 'Nenhuma conta selecionada ainda. Escolha a conta do Google Ads que deseja rastrear.',
      selectConversionTitle: 'Selecionar ação de conversão',
      selectConversionDesc: 'Selecione uma ação de conversão existente ou crie uma nova para rastrear compras.',
      availableActions: 'Ações de conversão disponíveis',
      noActions: 'Nenhuma ação de conversão encontrada. Clique em "Criar novo" para criar uma.',
      tagBasedAction: 'Ação baseada em tag: não pode receber envios do lado do servidor. Escolha uma ação Trackful ou crie uma.',
      changeAction: 'Alterar ação',
      conflictTitle: 'Risco de contagem dupla detectado',
      conflictDesc: 'Estas outras ações de conversão de Compra também estão definidas como Principais na sua conta do Google Ads. Enquanto várias ações de compra forem principais, suas compras são contadas duas vezes na coluna Conversões e nos lances:',
      conflictFix: 'Tornar Trackful a única ação principal',
      conflictKeep: 'Manter como está',
      conflictConfirmDesc: 'As ações abaixo passarão a Secundárias (observação) na sua conta do Google Ads. Elas continuam sendo rastreadas, mas não contam mais na coluna Conversões nem nos lances. Reversível a qualquer momento.',
      conflictFixed: 'Pronto. As seguintes ações agora são apenas de observação:',
      conflictUndo: 'Desfazer',
      disconnectTitle: 'Desconectar Google Ads',
      disconnectConfirm: 'Tem certeza de que deseja desconectar o Google Ads? O rastreamento de conversões será interrompido até a reconexão.',
      disconnectRemoveActions: 'Remover também as ações de conversão criadas pelo Trackful no Google Ads',
      disconnectRemoveActionsHelp: 'Elas passarão ao status “Removida”: param de registrar, mas seus dados históricos permanecem nos relatórios.',
      testSuccess: 'Teste bem-sucedido! A configuração é válida.',
      testRequestId: 'ID de solicitação do Google',
      testFailed: 'O teste falhou',
      enableTracking: 'Ativar rastreamento',
      disableTracking: 'Desativar rastreamento',
      actionFailed: 'Ocorreu um erro. Tente novamente.',
    },
    meta: {
      title: 'Meta CAPI',
      subtitle: 'Configure a Meta Conversions API para rastreamento server-side',
      connectionStatus: 'Status da conexão',
      pixelId: 'ID do Pixel',
      testEventCode: 'Código de evento de teste',
      lastSync: 'Última sincronização',
      sendTestEvent: 'Enviar evento de teste',
      updateConfiguration: 'Alterar configuração',
      configureMeta: 'Configurar Meta CAPI',
      connectDescription: 'Conecte seu Meta Pixel para iniciar o rastreamento de conversões pela API de Conversões.',
      emq: 'Qualidade de correspondência de eventos (EMQ)',
      emqDesc: 'O EMQ mede o quanto seus dados de eventos correspondem aos usuários do Meta. Pontuações mais altas significam melhor atribuição e desempenho de anúncios.',
      overallScore: 'Pontuação geral',
      recommendations: 'Recomendações',
      emqNotAvailable: 'Dados EMQ não disponíveis. Envie eventos e atualize para ver as pontuações.',
      setupInstructions: 'Instruções de configuração',
      step1: '1. Acesse o Gerenciador de Eventos do Meta',
      step2: '2. Encontre o ID do Pixel (Configurações > ID do Pixel)',
      step3: '3. Gere um Token de acesso (Configurações > API de Conversões > Gerar Token)',
      step4: '4. Opcionalmente, crie um Código de evento de teste para depuração',
      step5: '5. Insira esses valores no formulário de configuração',
      configureTitle: 'Configurar Meta CAPI',
      saveConfiguration: 'Salvar configuração',
      pixelIdPlaceholder: '1234567890123456',
      pixelIdHelp: 'Seu ID do Meta Pixel do Gerenciador de Eventos',
      accessToken: 'Token de acesso',
      accessTokenPlaceholder: 'EAAx...',
      accessTokenHelp: 'Token de acesso da API de Conversões (mantenha em segredo)',
      testEventCodeOptional: 'Código de evento de teste (Opcional)',
      testEventCodePlaceholder: 'TEST12345',
      testEventCodeHelp: 'Use isso para enviar eventos para Test Events no Gerenciador de Eventos',
      disconnectTitle: 'Desconectar Meta CAPI',
      disconnectConfirm: 'Tem certeza de que deseja desconectar o Meta CAPI? O rastreamento de conversões será interrompido até a reconexão.',
      testSuccess: 'Evento de teste enviado com sucesso! Eventos recebidos:',
      testFailed: 'O teste falhou',
      required: 'O ID do Pixel e o Token de acesso são obrigatórios',
      enableTracking: 'Ativar rastreamento',
      disableTracking: 'Desativar rastreamento',
      actionFailed: 'Ocorreu um erro. Tente novamente.',
    },
    billing: {
      title: 'Faturamento',
      subtitle: 'Gerencie sua assinatura e acompanhe seu uso',
      currentPlan: 'Plano atual',
      usage: 'Uso',
      ordersUsed: 'Pedidos rastreados',
      ordersRemaining: 'Eventos restantes',
      unlimited: 'Ilimitado',
      ofLimit: 'do limite',
      trial: 'Teste gratuito',
      trialActive: 'Teste ativo',
      trialEndsIn: 'O teste termina em',
      trialDaysRemaining: 'restantes',
      days: 'dias',
      upgradeNow: 'Fazer upgrade',
      upgradePlan: 'Upgrade do plano',
      downgradePlan: 'Downgrade do plano',
      cancelSubscription: 'Cancelar assinatura',
      scheduledChange: 'Mudança para {plan} em {date}. Até lá você mantém {current}.',
      alreadyCancelled: 'Cancelado. O acesso vai até o fim do período.',
      // Billing cadence. `savePercent` and `billedYearly` are templates:
      // the numbers come from the API so the page can never quote a
      // discount the charge does not match.
      billMonthly: 'Mensal',
      billAnnually: 'Anual',
      savePercent: 'Economize {percent} %',
      billedYearly: 'cobrado {amount} por ano',
      switchToYearly: 'Mudar para anual',
      switchToMonthly: 'Mudar para mensal',
      selectPlan: 'Selecionar este plano',
      currentPlanBadge: 'Plano atual',
      month: '/mês',
      ordersPerMonth: 'pedidos rastreados/mês',
      subscribe: 'Assinar',
      havePartnerCode: 'Tem um código de parceiro?',
      partnerCodeApplied: 'Código de parceiro aplicado',
      partnerCodeAppliedHelp: 'Seu desconto é aplicado antes da confirmação, portanto o preço que a Shopify mostra já o inclui.',
      partnerCodePrompt: 'Se um parceiro lhe deu um código, insira-o antes de assinar.',
      partnerCodeFirstOnly: 'Um código de parceiro é inserido antes da sua primeira assinatura. Um desconto em curso acompanha você ao mudar de plano, desde que mantenha a mesma periodicidade.',
      partnerCodePlaceholder: 'Opcional',
      partnerCodeLabel: 'Código de parceiro',
      partnerCodeHelp: 'Aplicado ao seu primeiro mês. Um código desconhecido significa apenas nenhum desconto.',
      sectionChannels: 'Canais de publicidade',
      sectionTracking: 'Rastreamento e atribuição',
      sectionCompliance: 'Conformidade e consentimento',
      sectionInsights: 'Dados e insights',
      sectionSupport: 'Suporte',
      features: 'Recursos',
      plans: 'Planos disponíveis',
      starter: 'Starter',
      growth: 'Growth',
      pro: 'Pro',
      warningNearLimit: 'Você está se aproximando do seu limite mensal de eventos.',
      warningOverLimit: 'Você excedeu seu limite de eventos. Faça upgrade para continuar o rastreamento.',
      warningTrialEnding: 'Seu teste está terminando. Assine para continuar.',
      billingHistory: 'Histórico de faturamento',
      noHistory: 'Sem histórico de faturamento',
      recommendation: 'Recomendação',
      upgradeToRecommended: 'Upgrade para {plan}',
      plansLoadError: 'Não foi possível carregar os planos. Atualize a página.',
      actionFailed: 'Erro ao alterar o plano. Tente novamente.',
      subscriptionSuccess: 'Assinatura ativada com sucesso! Seu rastreamento está agora ativo.',
      planNowSuccess: 'Pronto. Você está agora no plano {plan}.',
      scheduledSuccess: 'Confirmado. Você passa para {plan} no fim do período que já pagou.',
      scheduledBadge: 'Agendado',
      cancelConfirmationTrial: 'Você ainda está no teste gratuito, nada foi cobrado. Cancelar coloca você de volta no plano Free imediatamente.',
      activationFailed: 'Não foi possível confirmar sua assinatura. Tente novamente ou contate o suporte.',
      planName: 'Plano',
      priceLabel: 'Preço',
      upgrade: 'Atualizar',
      downgrade: 'Rebaixar',
      historyDate: 'Data',
      historyType: 'Tipo',
      historyAmount: 'Valor',
      historyStatus: 'Status',
      cancelTitle: 'Cancelar assinatura',
      confirmCancel: 'Sim, cancelar',
      keepPlan: 'Manter meu plano',
      cancelConfirmation: 'Tem certeza de que deseja cancelar? Seu plano permanecerá ativo até o final do período de cobrança atual.',
      featureLabels: {
        google_ads: 'Google Ads Enhanced Conversions',
        meta_capi: 'Meta Conversions API',
        identity_resolution: 'Resolução de identidade',
        basic_support: 'Suporte básico',
        priority_support: 'Suporte prioritário',
        dedicated_support: 'Suporte dedicado',
        advanced_analytics: 'Analytics avançado',
        api_access: 'Acesso à API',
        ga4: 'Google Analytics 4 (Measurement Protocol)',
        tiktok: 'TikTok Events API',
        pinterest: 'Pinterest Conversions API',
        snapchat: 'Snapchat Conversions API',
        reconciliation: 'Reconciliação de conversões',
        live_debugger: 'Depurador de eventos em tempo real',
        alerts: 'Alertas de rastreamento',
        export_csv: 'Exportação CSV',
        webhooks_out: 'Webhooks de saída',
        server_side_tracking: 'Rastreamento server-side (contorna bloqueadores de anúncios)',
        one_year_cookies: 'Cookies de servidor de 1 ano (contorna o Safari ITP)',
        click_id_capture: 'Captura de click IDs (gclid, fbclid, ttclid, etc.)',
        full_funnel: 'Envio do funil completo (visualizações, carrinhos, checkouts)',
        dedup: 'Deduplicação determinística de eventos',
        auto_backfill: 'Reenvio automático após uma pausa (cota, canal desativado)',
        consent_mode: 'Google Consent Mode v2',
        cmp_autodetect: 'Detecção automática de CMP (6 provedores)',
        gdpr: 'Conforme com o GDPR',
        realtime_dashboard: 'Painel em tempo real',
        emq_monitoring: 'Monitoramento EMQ da Meta',
        all: 'Todos os recursos',
      },
    },
    settings: {
      title: 'Configurações',
      subtitle: 'Configure as opções da sua loja',
      shopInfo: 'Informações da loja',
      shopDomain: 'Domínio',
      shopName: 'Nome',
      shopId: 'ID Trackful',
      createdAt: 'Criado em',
      consentSettings: 'Configurações de consentimento',
      consentDesc: 'Configure as opções padrão de consentimento para o Consent Mode v2. Esses valores são usados quando o visitante ainda não deu seu consentimento.',
      defaultConsent: 'Consentimento padrão',
      adStorage: 'Armazenamento publicitário',
      adStorageHelp: 'Permite armazenar cookies para publicidade',
      analyticsStorage: 'Armazenamento de analytics',
      analyticsStorageHelp: 'Permite armazenar cookies para análise',
      adUserData: 'Dados de usuário pub.',
      adUserDataHelp: 'Permite enviar dados do usuário para publicidade',
      adPersonalization: 'Personalização pub.',
      adPersonalizationHelp: 'Permite a personalização de anúncios',
      granted: 'Concedido',
      denied: 'Negado',
      unspecified: 'Não especificado (recomendado se você usa um banner de consentimento)',
      saveConsent: 'Salvar consentimento',
      saveFailed: 'Erro ao salvar',
      language: 'Idioma',
      languageHelp: 'Selecione o idioma da interface',
      french: 'Francês',
      english: 'Inglês',
      german: 'Alemão',
      spanish: 'Espanhol',
      portugueseBR: 'Português (Brasil)',
      japanese: 'Japonês',
      dutch: 'Holandês',
    },
    backfillHold: {
      titleOne: '{n} pedido anterior está pronto para ser enviado às suas plataformas de anúncios',
      titleMany: '{n} pedidos anteriores estão prontos para serem enviados às suas plataformas de anúncios',
      action: 'Enviar para minhas plataformas',
      actionPending: 'Enviando...',
      imported: 'Importamos seus pedidos dos últimos 90 dias quando você instalou o Trackful. Eles já estão contabilizados neste painel e no Google Analytics 4.',
      notSent: 'Não os enviamos para Google Ads, Meta, TikTok, Pinterest nem Snapchat. São suas contas de anúncios, e se outra ferramenta de rastreamento já reportou esses mesmos pedidos, enviá-los de novo duplicaria seu histórico. Nada é enviado até você decidir.',
      windows: 'Somente pedidos recentes serão aceitos: a Meta recusa qualquer coisa com mais de 7 dias, e o Google Ads cerca de 90. Os mais antigos permanecem no seu painel de qualquer forma.',
      error: 'Algo deu errado no envio. Nada foi perdido, você pode tentar novamente.',
      success: 'Enviados. Eles aparecerão nas suas plataformas nos próximos minutos.',
    },
    competingPixels: {
      criticalTitle: 'Suas compras provavelmente estão sendo contadas duas vezes na Meta',
      criticalAction: 'Abrir configurações da Meta',
      criticalDetected: 'Detectamos algo parecido com {sources} na sua loja. Ele envia os próprios eventos de compra para a Meta, e o Trackful envia essas mesmas compras pelo servidor. Essas duas fontes não podem ser desduplicadas: o canal de vendas escolhe os próprios identificadores de evento e não os expõe, então a Meta não tem como diferenciar os dois.',
      criticalAdvice: 'Mantenha uma fonte, não as duas. Recomendamos manter o Trackful e desligar o rastreamento do canal de vendas, porque o Trackful também reporta as compras que um pixel de navegador perde por bloqueadores de anúncios, restrições do iOS e checkouts hospedados. Se preferir manter o canal de vendas, desative a Meta no Trackful.',
      warningTitle: 'Um pixel de navegador está rodando junto com o Trackful',
      warningDetected: 'Detectamos {sources} na sua loja. Esse pixel e o Trackful alcançam a mesma conta de anúncios, então a mesma compra pode ser contada duas vezes.',
      warningDedup: 'Essas fontes são configuradas por você, então a desduplicação é possível: envie o identificador de evento do Trackful junto com o evento do navegador. Em uma compra, nosso identificador é o {strong}.',
      warningDedupStrong: 'número do pedido',
      itemMeta: 'Pixel da Meta: defina {code} como o número do pedido no seu evento Purchase do navegador.',
      itemOthers: 'TikTok, Pinterest e Snap: defina {code} como o número do pedido.',
      itemGtm: 'Google Tag Manager: repasse o número do pedido para as tags que o contêiner dispara.',
      selfDeduping: '{sources} pode ficar como está: o Google Ads desduplica sozinho pelo número do pedido.',
      lastDetected: 'Última detecção na sua loja: {date}',
    },
  },

  // ===========================================================================
  // 日本語 (Japanese)
  // ===========================================================================
  ja: {
    nav: {
      dashboard: 'ダッシュボード',
      googleAds: 'Google Ads',
      meta: 'Meta CAPI',
      billing: '請求',
      settings: '設定',
    },
    common: {
      save: '保存',
      cancel: 'キャンセル',
      connect: '接続',
      disconnect: '切断',
      enable: '有効にする',
      disable: '無効にする',
      test: 'テスト',
      refresh: '更新',
      loading: '読み込み中...',
      error: 'エラー',
      success: '成功',
      warning: '警告',
      connected: '接続済み',
      notConnected: '未接続',
      active: 'アクティブ',
      disabled: '無効',
      pending: '保留中',
      sent: '送信済み',
      thisMonth: '今月',
      selectAccount: 'アカウントを選択',
      selectAction: 'アクションを選択',
      createNew: '新規作成',
      actionFailed: 'エラーが発生しました。もう一度お試しください。',
      configFailed: '設定に失敗しました。もう一度お試しください。',
      gateNeedsPlan: 'この機能には {plan} プランが必要です',
      gateNeedsSubscription: 'この機能を利用するにはサブスクリプションを有効にしてください',
      gateBody: 'データの収集は通常どおり継続されます。{plan} プランにアップグレードすると、数クリックでこの機能を有効化できます。',
      gateCta: 'プランを見る',
      disableChannelTitle: '{channel} への送信を無効にしますか？',
      disableChannelBody: 'キャプチャは100%継続されます。すべてのコンバージョンは引き続き Trackful に記録されます。{channel} への送信のみが一時停止されます。',
      disableChannelSafety: '再有効化すると、一時停止中に記録されたすべてのコンバージョンが自動的に {channel} へ再送信されます。確認メールが送信されます。',
    },
    dashboard: {
      title: 'ダッシュボード',
      totalConversions: 'コンバージョン合計',
      totalRevenue: '売上合計',
      googleAdsTracked: 'Google Ads トラッキング済み',
      deliveryRate: '広告プラットフォームへ送信済み',
      metaTracked: 'Meta トラッキング済み',
      ofConversions: 'のコンバージョン',
      attributionRate: 'アトリビューション率',
      conversionsWithAttribution: 'クリックIDアトリビューション付きコンバージョン',
      excellent: '優秀',
      good: '良好',
      needsImprovement: '改善が必要',
      integrationStatus: '連携ステータス',
      eventMatchQuality: 'Meta イベントマッチ品質',
      emqNotAvailable: 'EMQデータがありません。Meta CAPIを接続してイベントを送信してください。',
      recentConversions: '最近のコンバージョン',
      deliveryPerChannel: 'チャネル別の送信状況：すべてのコンバージョンは有効な全チャネルに送信され、各プラットフォームが独自にアトリビューションを行います。',
      noConversions: 'コンバージョンなし',
      noConversionsDesc: 'トラッキングが有効な注文を受け取ると、ここにコンバージョンが表示されます。',
      order: '注文',
      revenue: '売上',
      googleAds: 'Google Ads',
      meta: 'Meta',
      attribution: 'アトリビューション',
      date: '日付',
      warningNotConnected: 'Google AdsもMeta CAPIも接続されていません。トラッキングを開始するために連携を設定してください。',
      warningGoogleNotConnected: 'Google Adsが接続されていません。コンバージョンをトラッキングするために設定してください。',
      warningMetaNotConnected: 'Meta CAPIが接続されていません。コンバージョンをトラッキングするために設定してください。',
      integrationLoadError: '連携ステータスを読み込めませんでした。ページを更新してください。',
      failedThisMonth: '今月の失敗',
      yes: 'はい',
      no: 'いいえ',
      direct: 'ダイレクト',
    },
    setup: {
      activeTitle: 'エンタープライズ級のサーバーサイド計測が有効です',
      activeBadge: '有効',
      activeBody: '他に必要な操作はありません。残りは当社が対応します：イベントの取得、個人情報のハッシュ化、各広告プラットフォームへの送信、エラー時の自動復旧。',
      activeLiveChannels: '稼働中のチャネル',
      inactiveTitle: 'サーバーサイド計測を有効化',
      inactiveBody: '少なくとも1つの広告プラットフォームを接続してください。1つのチャネルで開始できます。裏側の設定（コンバージョンアクション、ピクセル、アトリビューション）は Trackful がすべて自動で行います。',
      connect: '接続',
      connected: '接続済み',
      needsAction: '完了する',
      connectFailed: '接続に失敗しました。もう一度お試しください。',
      pixelLive: 'ピクセルが有効、イベントを取得中',
      pixelWaiting: '最初のピクセルイベントを待機中',
      clickTrackerTitle: 'テーマでクリックトラッキングを有効化',
      clickTrackerActive: '有効',
      clickTrackerCta: '今すぐ有効化',
      clickTrackerBody: 'クリックトラッカーは広告識別子（gclid、fbclid など）を取得し、1 年間有効なサーバー Cookie を設定します。正確なアトリビューションの前提条件です。Shopify では自動有効化できないため、「有効化」をクリックし、テーマエディタで保存してください。',
      clickTrackerHint: '保存後、ストアを一度開いてください。このステップは自動的に緑になります。',
      sendTestEvent: 'テストコンバージョンを送信',
      testEventOk: 'Google がテストコンバージョンを受理しました ✓',
      testEventFail: 'テストに失敗しました。詳細は Google Ads ページをご覧ください。',
      consentNotice: 'コンプライアンス：Cookie バナー（Consent Mode v2）がない場合、EU のコンバージョンは顧客データを Meta や GA4 に送信できません。CMP を設定してください。',
      advanced: '詳細オプション',
      funnelTitle: 'フルファネル転送',
      funnelBody: '購入だけでなく、購買までの行動全体（商品閲覧、カート追加、チェックアウト）を追加の最適化シグナルとして広告プラットフォームに送信します。購入は常に送信されます。',
      funnelOn: 'オン',
      funnelOff: 'オフ',
      funnelChannelsTitle: '各プラットフォームが受け取るもの',
      funnelChannelsBody: '購入は常に送信されます。それ以外の行動（商品閲覧、カート追加、チェックアウト）は、二重にカウントされないと保証できる場合にのみ送信します。',
      funnelSends: '全ファネル',
      funnelPurchasesOnly: '購入のみ',
      funnelWhyOwn: '当社独自のコンバージョンアクションです。お客様側のものが二重にカウントされることはありません。',
      funnelWhyStamped: 'お客様のタグ（{sources}）に当社のイベントIDが付与されたため、プラットフォームは1件として数えます。',
      funnelWhyEmpty: 'このプロパティは当社の接続前に何も受信していませんでした。',
      funnelWhyDeclared: 'Trackful が唯一の送信元であるとご申告いただきました。',
      funnelWhyCollision: '別のソースがすでにこのプラットフォームに送信しています（{sources}）。数値が二重にならないよう、購入のみを送信します。',
      funnelAsk: '他のタグがブラウザからこのプラットフォームへイベントを送信していますか。Shopify 標準の販売チャネルも含みます。',
      funnelAskKeep: 'はい、残します',
      funnelAskOnly: 'いいえ、Trackful のみです',
      funnelAskWhy: 'これは当社からは確認できません。Shopify 標準チャネルのピクセルは分離されたサンドボックスで動作するためです。',
      pausedTitle: '送信を一時停止中（データは保持されています）',
      pausedCta: 'アップグレード',
      pausedBody: 'プランの上限に達しました。すべてのイベントは引き続き取得していますが、広告プラットフォームへの送信を一時停止しています。アップグレードすると再開し、取得済みのデータをすべて自動的に再送信します。',
      pausedBodyCount: 'プランの上限に達しました。{count} 件のコンバージョンを取得済みで送信待ちです。すべての取得は継続中です。アップグレードすると送信を再開し、すべて自動的に再送信します。',
    },
    googleAds: {
      title: 'Google Ads',
      subtitle: 'Enhanced Conversionsトラッキングのため Google Adsを接続',
      connectionStatus: '接続ステータス',
      account: 'アカウント',
      conversionAction: 'コンバージョンアクション',
      lastSync: '最終同期',
      testConnection: '接続テスト',
      connectGoogleAds: 'Google Adsを接続',
      connectDescription: 'Google Adsアカウントを接続して、Enhanced Conversionsによるコンバージョントラッキングを開始します。',
      enhancedConversions: 'Enhanced Conversions',
      enhancedConversionsDesc: 'Enhanced Conversionsは、ハッシュ化された顧客データ（メール、電話、住所）を安全にGoogle Adsに送信し、トラッキングの精度を向上させます。',
      enableEnhancedConversions: 'Enhanced Conversionsを有効にする',
      enhancedConversionsHelp: 'GDPR/Consent Mode v2に基づくad_user_dataの顧客同意が必要です',
      ecActionRequiredTitle: 'Google Ads での対応が必要です',
      ecActionRequiredIntro: 'Enhanced Conversions はこのアプリから有効化できません。Google Ads（目標 > コンバージョン > 設定）で設定してください：',
      ecStep1: '1.「拡張コンバージョン（ウェブ）」をオンにする',
      ecStep2: '2. 顧客データ規約に同意する',
      ecStep3: '3. 方法として「Google Ads API」を選択する',
      ecNote: 'これらの手順が完了するまで、送信されたハッシュ化済み顧客データは Google で処理されません。',
      ecReady: 'Enhanced Conversions の準備が整いました。この Google Ads アカウントで顧客データ規約に同意済みです。',
      conversionActionsTitle: 'コンバージョンアクション',
      conversionActionsDesc: 'Trackful は購入（主要アクション）を送信し、ファネルの各ステップを補助アクションとして送信することもできます。',
      autoCreatedNote: 'アカウント選択時に Google Ads アカウント内へ自動作成されました。目標 > コンバージョンで確認できます。',
      funnelOffHint: 'フルファネル転送はオフです（ダッシュボード > 詳細オプション）。これらのスイッチは、オンにすると二次イベントに適用されます。購入は常に送信されます。',
      disableTitle: 'Google Ads への送信を無効にしますか？',
      disableConfirmBody: 'キャプチャは100%継続されます。すべてのコンバージョンは引き続き Trackful に記録されます。Google Ads への送信のみが一時停止されます。',
      disableConfirmSafety: '再有効化すると、一時停止中に記録されたすべてのコンバージョンが自動的に Google Ads へ再送信されます。確認メールが送信されます。',
      funnelPurchase: '購入',
      funnelPurchaseDesc: '最も重要なイベント（売上）。常に送信されます。',
      primaryBadge: '主要',
      createPrimaryAction: '購入アクションを作成',
      activateAction: '有効化',
      funnelProductView: '商品閲覧',
      funnelProductViewDesc: '訪問者が商品ページを閲覧したとき。',
      funnelAddToCart: 'カート追加',
      funnelAddToCartDesc: '商品がカートに追加されたとき。強い購入意欲のシグナル。',
      funnelBeginCheckout: '決済開始',
      funnelBeginCheckoutDesc: '訪問者が決済を開始したとき。',
      funnelAddPaymentInfo: '支払情報の入力',
      funnelAddPaymentInfoDesc: '支払情報が入力されたとき。',
      setupInstructions: '設定手順',
      step1: '1. 「Google Adsを接続」をクリックしてアクセスを許可します',
      step2: '2. 使用するGoogle Adsアカウントを選択します',
      step3: '3. 購入用のコンバージョンアクションを選択または作成します',
      step4: '4. より高い精度のためにEnhanced Conversionsを有効にします',
      selectAccountTitle: 'Google Adsアカウントを選択',
      availableAccounts: '利用可能なアカウント',
      noAccounts: 'アカウントが見つかりません。GoogleアカウントがGoogle Adsアカウントにアクセスできることを確認してください。',
      selectAccountPrompt: 'アカウントがまだ選択されていません。追跡する Google 広告アカウントを選択してください。',
      selectConversionTitle: 'コンバージョンアクションを選択',
      selectConversionDesc: '既存のコンバージョンアクションを選択するか、購入トラッキング用に新しいものを作成します。',
      availableActions: '利用可能なコンバージョンアクション',
      noActions: 'コンバージョンアクションが見つかりません。「新規作成」をクリックして作成してください。',
      tagBasedAction: 'タグベースのアクション：サーバーサイドのアップロードを受信できません。Trackful のアクションを選択するか、新規作成してください。',
      changeAction: 'アクションを変更',
      conflictTitle: '重複カウントのリスクを検出しました',
      conflictDesc: 'Google Ads アカウントで、他の購入コンバージョンアクションもプライマリに設定されています。複数の購入アクションがプライマリのままだと、購入がコンバージョン列と入札で二重にカウントされます：',
      conflictFix: 'Trackful を唯一のプライマリにする',
      conflictKeep: 'このままにする',
      conflictConfirmDesc: '以下のアクションは Google Ads アカウントでセカンダリ（モニタリング）に設定されます。引き続き計測されますが、コンバージョン列や入札にはカウントされません。いつでも元に戻せます。',
      conflictFixed: '完了。以下のアクションはモニタリングのみになりました：',
      conflictUndo: '元に戻す',
      disconnectTitle: 'Google Adsを切断',
      disconnectConfirm: 'Google Adsを切断してもよろしいですか？再接続するまでコンバージョントラッキングは停止します。',
      disconnectRemoveActions: 'Trackful が作成したコンバージョンアクションも Google Ads から削除する',
      disconnectRemoveActionsHelp: 'ステータスが「削除済み」になります。記録は停止しますが、過去のデータはレポートに残ります。',
      testSuccess: 'テスト成功！設定は有効です。',
      testRequestId: 'Google リクエスト ID',
      testFailed: 'テスト失敗',
      enableTracking: 'トラッキングを有効にする',
      disableTracking: 'トラッキングを無効にする',
      actionFailed: 'エラーが発生しました。もう一度お試しください。',
    },
    meta: {
      title: 'Meta CAPI',
      subtitle: 'サーバーサイドトラッキングのためMeta Conversions APIを設定',
      connectionStatus: '接続ステータス',
      pixelId: 'ピクセルID',
      testEventCode: 'テストイベントコード',
      lastSync: '最終同期',
      sendTestEvent: 'テストイベントを送信',
      updateConfiguration: '設定を変更',
      configureMeta: 'Meta CAPIを設定',
      connectDescription: 'Meta Pixelを接続して、Conversions APIによるコンバージョントラッキングを開始します。',
      emq: 'イベントマッチ品質（EMQ）',
      emqDesc: 'EMQは、イベントデータがMetaユーザーとどの程度一致するかを測定します。スコアが高いほど、アトリビューションと広告パフォーマンスが向上します。',
      overallScore: '総合スコア',
      recommendations: '推奨事項',
      emqNotAvailable: 'EMQデータがありません。イベントを送信して更新すると、スコアが表示されます。',
      setupInstructions: '設定手順',
      step1: '1. Metaイベントマネージャにアクセスします',
      step2: '2. ピクセルIDを見つけます（設定 > ピクセルID）',
      step3: '3. アクセストークンを生成します（設定 > Conversions API > トークンを生成）',
      step4: '4. オプションでデバッグ用のテストイベントコードを作成します',
      step5: '5. これらの値を設定フォームに入力します',
      configureTitle: 'Meta CAPIを設定',
      saveConfiguration: '設定を保存',
      pixelIdPlaceholder: '1234567890123456',
      pixelIdHelp: 'イベントマネージャからのMeta PixelID',
      accessToken: 'アクセストークン',
      accessTokenPlaceholder: 'EAAx...',
      accessTokenHelp: 'Conversions APIアクセストークン（秘密にしてください）',
      testEventCodeOptional: 'テストイベントコード（オプション）',
      testEventCodePlaceholder: 'TEST12345',
      testEventCodeHelp: 'イベントマネージャのテストイベントにイベントを送信するために使用します',
      disconnectTitle: 'Meta CAPIを切断',
      disconnectConfirm: 'Meta CAPIを切断してもよろしいですか？再接続するまでコンバージョントラッキングは停止します。',
      testSuccess: 'テストイベントが正常に送信されました！受信イベント：',
      testFailed: 'テスト失敗',
      required: 'ピクセルIDとアクセストークンが必要です',
      enableTracking: 'トラッキングを有効にする',
      disableTracking: 'トラッキングを無効にする',
      actionFailed: 'エラーが発生しました。もう一度お試しください。',
    },
    billing: {
      title: '請求',
      subtitle: 'サブスクリプションを管理し、使用状況を確認',
      currentPlan: '現在のプラン',
      usage: '使用状況',
      ordersUsed: '計測済み注文数',
      ordersRemaining: '残りイベント数',
      unlimited: '無制限',
      ofLimit: '上限の',
      trial: '無料トライアル',
      trialActive: 'トライアル中',
      trialEndsIn: 'トライアル終了まで',
      trialDaysRemaining: '残り',
      days: '日',
      upgradeNow: 'アップグレード',
      upgradePlan: 'プランをアップグレード',
      downgradePlan: 'プランをダウングレード',
      cancelSubscription: 'サブスクリプションをキャンセル',
      scheduledChange: '{date} に {plan} へ変更されます。それまでは {current} のままです。',
      alreadyCancelled: 'キャンセル済み。期間終了までご利用いただけます。',
      // Billing cadence. `savePercent` and `billedYearly` are templates:
      // the numbers come from the API so the page can never quote a
      // discount the charge does not match.
      billMonthly: '月額',
      billAnnually: '年額',
      savePercent: '{percent}%お得',
      billedYearly: '年間 {amount} 請求',
      switchToYearly: '年額に切り替える',
      switchToMonthly: '月額に切り替える',
      selectPlan: 'このプランを選択',
      currentPlanBadge: '現在のプラン',
      month: '/月',
      ordersPerMonth: '計測注文/月',
      subscribe: '登録する',
      havePartnerCode: 'パートナーコードをお持ちですか？',
      partnerCodeApplied: 'パートナーコードが適用されました',
      partnerCodeAppliedHelp: '割引は確認前に適用されるため、Shopify に表示される価格にはすでに含まれています。',
      partnerCodePrompt: 'パートナーからコードを受け取っている場合は、登録前に入力してください。',
      partnerCodeFirstOnly: 'パートナーコードは最初のご登録の前に入力します。適用中の割引は、請求サイクルが同じであればプラン変更後も引き継がれます。',
      partnerCodePlaceholder: '任意',
      partnerCodeLabel: 'パートナーコード',
      partnerCodeHelp: '初月に適用されます。不明なコードの場合は割引が適用されないだけです。',
      sectionChannels: '広告チャネル',
      sectionTracking: 'トラッキングとアトリビューション',
      sectionCompliance: 'コンプライアンスと同意',
      sectionInsights: 'データとインサイト',
      sectionSupport: 'サポート',
      features: '機能',
      plans: '利用可能なプラン',
      starter: 'Starter',
      growth: 'Growth',
      pro: 'Pro',
      warningNearLimit: '月間イベント上限に近づいています。',
      warningOverLimit: 'イベント上限を超えました。トラッキングを続けるにはアップグレードしてください。',
      warningTrialEnding: 'トライアルがまもなく終了します。継続するにはご購読ください。',
      billingHistory: '請求履歴',
      noHistory: '請求履歴なし',
      recommendation: 'おすすめ',
      upgradeToRecommended: '{plan}にアップグレード',
      plansLoadError: 'プランを読み込めませんでした。ページを更新してください。',
      actionFailed: 'プラン変更に失敗しました。もう一度お試しください。',
      subscriptionSuccess: 'サブスクリプションが正常に有効化されました！トラッキングが開始されます。',
      planNowSuccess: '完了しました。現在のプランは {plan} です。',
      scheduledSuccess: '受け付けました。お支払い済みの期間の終了時に {plan} へ変更されます。',
      scheduledBadge: '変更予定',
      cancelConfirmationTrial: '現在は無料トライアル中で、請求は発生していません。キャンセルすると、すぐに Free プランに戻ります。',
      activationFailed: 'サブスクリプションを確認できませんでした。もう一度お試しいただくか、サポートまでご連絡ください。',
      planName: 'プラン',
      priceLabel: '価格',
      upgrade: 'アップグレード',
      downgrade: 'ダウングレード',
      historyDate: '日付',
      historyType: 'タイプ',
      historyAmount: '金額',
      historyStatus: 'ステータス',
      cancelTitle: 'サブスクリプションをキャンセル',
      confirmCancel: 'はい、キャンセルします',
      keepPlan: 'プランを維持',
      cancelConfirmation: '本当にキャンセルしますか？現在の請求期間終了までプランは有効です。',
      featureLabels: {
        google_ads: 'Google Ads Enhanced Conversions',
        meta_capi: 'Meta Conversions API',
        identity_resolution: 'アイデンティティ解決',
        basic_support: 'ベーシックサポート',
        priority_support: '優先サポート',
        dedicated_support: '専任サポート',
        advanced_analytics: '高度な分析',
        api_access: 'APIアクセス',
        ga4: 'Google アナリティクス 4（Measurement Protocol）',
        tiktok: 'TikTok Events API',
        pinterest: 'Pinterest Conversions API',
        snapchat: 'Snapchat Conversions API',
        reconciliation: 'コンバージョンの照合',
        live_debugger: 'リアルタイムイベントデバッガー',
        alerts: 'トラッキングアラート',
        export_csv: 'CSV エクスポート',
        webhooks_out: '送信 Webhook',
        server_side_tracking: 'サーバーサイドトラッキング（広告ブロッカーを回避）',
        one_year_cookies: '1年間のサーバーCookie（Safari ITP 回避）',
        click_id_capture: 'クリックID の取得（gclid、fbclid、ttclid など）',
        full_funnel: 'フルファネル転送（閲覧、カート、チェックアウト）',
        dedup: '決定論的なイベント重複排除',
        auto_backfill: '一時停止後の自動再送信（上限超過、チャネル無効化）',
        consent_mode: 'Google 同意モード v2',
        cmp_autodetect: 'CMP 自動検出（6 プロバイダー）',
        gdpr: 'GDPR 準拠',
        realtime_dashboard: 'リアルタイムダッシュボード',
        emq_monitoring: 'Meta EMQ モニタリング',
        all: 'すべての機能',
      },
    },
    settings: {
      title: '設定',
      subtitle: 'ショップの設定を構成します',
      shopInfo: 'ショップ情報',
      shopDomain: 'ドメイン',
      shopName: '名前',
      shopId: 'Trackful ID',
      createdAt: '作成日',
      consentSettings: '同意設定',
      consentDesc: 'Consent Mode v2のデフォルト同意設定を構成します。これらの値は、訪問者がまだ同意していない場合に使用されます。',
      defaultConsent: 'デフォルトの同意',
      adStorage: '広告ストレージ',
      adStorageHelp: '広告用Cookieの保存を許可',
      analyticsStorage: 'アナリティクスストレージ',
      analyticsStorageHelp: '分析用Cookieの保存を許可',
      adUserData: '広告ユーザーデータ',
      adUserDataHelp: '広告用ユーザーデータの送信を許可',
      adPersonalization: '広告パーソナライゼーション',
      adPersonalizationHelp: '広告のパーソナライゼーションを許可',
      granted: '許可',
      denied: '拒否',
      unspecified: '未指定（同意バナーを使用している場合に推奨）',
      saveConsent: '同意を保存',
      saveFailed: '保存に失敗しました',
      language: '言語',
      languageHelp: 'インターフェースの言語を選択',
      french: 'フランス語',
      english: '英語',
      german: 'ドイツ語',
      spanish: 'スペイン語',
      portugueseBR: 'ポルトガル語（ブラジル）',
      japanese: '日本語',
      dutch: 'オランダ語',
    },
    backfillHold: {
      titleOne: '過去の注文 {n} 件を広告プラットフォームに送信できます',
      titleMany: '過去の注文 {n} 件を広告プラットフォームに送信できます',
      action: '広告プラットフォームに送信',
      actionPending: '送信中...',
      imported: 'Trackful のインストール時に、過去 90 日分の注文を取り込みました。これらはこのダッシュボードと Google アナリティクス 4 にはすでに反映されています。',
      notSent: 'Google 広告、Meta、TikTok、Pinterest、Snapchat にはまだ送信していません。これらはお客様の広告アカウントであり、別の計測ツールが同じ注文をすでに送信している場合、再送すると履歴が二重になります。お客様が選択するまで何も送信されません。',
      windows: '受け付けられるのは直近の注文のみです。Meta は 7 日より古いものを拒否し、Google 広告はおよそ 90 日です。それより古い注文も、ダッシュボードには残ります。',
      error: '送信に失敗しました。データは失われていません。もう一度お試しください。',
      success: '送信しました。数分以内に各広告プラットフォームに表示されます。',
    },
    competingPixels: {
      criticalTitle: 'Meta で購入が二重に計測されている可能性があります',
      criticalAction: 'Meta の設定を開く',
      criticalDetected: 'ストアで {sources} と思われるものを検出しました。これは独自の購入イベントを Meta に送信し、Trackful も同じ購入をサーバーサイドで送信しています。この 2 つは重複排除できません。販売チャネルは独自のイベント ID を発行し、それを公開しないため、Meta は 2 つのイベントを区別できません。',
      criticalAdvice: 'どちらか一方だけを残してください。Trackful を残し、販売チャネル側の計測をオフにすることをおすすめします。Trackful は、広告ブロッカー、iOS のトラッキング制限、外部ホストの決済画面によってブラウザピクセルが取りこぼす購入も送信できるためです。販売チャネルを残したい場合は、Trackful 側で Meta を無効にしてください。',
      warningTitle: 'ブラウザピクセルが Trackful と並行して動作しています',
      warningDetected: 'ストアで {sources} を検出しました。このピクセルと Trackful は同じ広告アカウントに送信するため、同一の購入が二重に計測される可能性があります。',
      warningDedup: 'これらはお客様が設定できるため、重複排除が可能です。ブラウザイベントに Trackful のイベント ID を付与してください。購入の場合、当社のイベント ID は{strong}です。',
      warningDedupStrong: '注文番号',
      itemMeta: 'Meta ピクセル: ブラウザの Purchase イベントで {code} に注文番号を設定します。',
      itemOthers: 'TikTok、Pinterest、Snap: {code} に注文番号を設定します。',
      itemGtm: 'Google タグ マネージャー: コンテナが配信する各ベンダータグに注文番号を渡します。',
      selfDeduping: '{sources} はそのままで問題ありません。Google 広告は注文番号で自動的に重複排除します。',
      lastDetected: 'ストアでの最終検出: {date}',
    },
  },

  // ===========================================================================
  // NEDERLANDS (Dutch)
  // ===========================================================================
  nl: {
    nav: {
      dashboard: 'Dashboard',
      googleAds: 'Google Ads',
      meta: 'Meta CAPI',
      billing: 'Facturering',
      settings: 'Instellingen',
    },
    common: {
      save: 'Opslaan',
      cancel: 'Annuleren',
      connect: 'Verbinden',
      disconnect: 'Ontkoppelen',
      enable: 'Inschakelen',
      disable: 'Uitschakelen',
      test: 'Testen',
      refresh: 'Vernieuwen',
      loading: 'Laden...',
      error: 'Fout',
      success: 'Gelukt',
      warning: 'Waarschuwing',
      connected: 'Verbonden',
      notConnected: 'Niet verbonden',
      active: 'Actief',
      disabled: 'Uitgeschakeld',
      pending: 'In behandeling',
      sent: 'Verzonden',
      thisMonth: 'Deze maand',
      selectAccount: 'Account selecteren',
      selectAction: 'Actie selecteren',
      createNew: 'Nieuw aanmaken',
      actionFailed: 'Er is iets misgegaan. Probeer het opnieuw.',
      configFailed: 'Configuratie mislukt. Probeer het opnieuw.',
      gateNeedsPlan: 'Deze functie vereist het {plan}-abonnement',
      gateNeedsSubscription: 'Activeer uw abonnement om deze functie te ontgrendelen',
      gateBody: 'Uw gegevensregistratie loopt gewoon door. Upgrade naar het {plan}-abonnement om deze functie in een paar klikken te activeren.',
      gateCta: 'Abonnementen bekijken',
      disableChannelTitle: 'Doorsturen naar {channel} uitschakelen?',
      disableChannelBody: 'De registratie blijft 100% doorlopen: elke conversie wordt nog steeds vastgelegd in Trackful. Alleen het doorsturen naar {channel} wordt gepauzeerd.',
      disableChannelSafety: 'Bij het opnieuw inschakelen worden alle tijdens de pauze vastgelegde conversies automatisch opnieuw naar {channel} gestuurd. U ontvangt een bevestigingsmail.',
    },
    dashboard: {
      title: 'Dashboard',
      totalConversions: 'Totaal conversies',
      totalRevenue: 'Totale omzet',
      googleAdsTracked: 'Google Ads bijgehouden',
      deliveryRate: 'Naar advertentieplatforms verzonden',
      metaTracked: 'Meta bijgehouden',
      ofConversions: 'van conversies',
      attributionRate: 'Attributiepercentage',
      conversionsWithAttribution: 'Conversies met click-ID-attributie',
      excellent: 'Uitstekend',
      good: 'Goed',
      needsImprovement: 'Verbetering nodig',
      integrationStatus: 'Integratiestatus',
      eventMatchQuality: 'Meta Event Match Quality',
      emqNotAvailable: 'EMQ-gegevens niet beschikbaar. Verbind Meta CAPI en verstuur events.',
      recentConversions: 'Recente conversies',
      deliveryPerChannel: 'Levering per kanaal: elke conversie wordt naar alle actieve kanalen gestuurd; elk platform voert daarna zijn eigen attributie uit.',
      noConversions: 'Geen conversies',
      noConversionsDesc: 'Conversies verschijnen hier zodra u bestellingen ontvangt met tracking ingeschakeld.',
      order: 'Bestelling',
      revenue: 'Omzet',
      googleAds: 'Google Ads',
      meta: 'Meta',
      attribution: 'Attributie',
      date: 'Datum',
      warningNotConnected: 'Noch Google Ads noch Meta CAPI zijn verbonden. Configureer uw integraties om tracking te starten.',
      warningGoogleNotConnected: 'Google Ads is niet verbonden. Configureer het om conversies bij te houden.',
      warningMetaNotConnected: 'Meta CAPI is niet verbonden. Configureer het om conversies bij te houden.',
      integrationLoadError: 'Kon integratiestatus niet laden. Vernieuw de pagina.',
      failedThisMonth: 'Mislukt deze maand',
      yes: 'Ja',
      no: 'Nee',
      direct: 'Direct',
    },
    setup: {
      activeTitle: 'Uw server-side enterprise-tracking is actief',
      activeBadge: 'Actief',
      activeBody: 'Niets anders te doen. Wij regelen de rest: het vastleggen van events, het hashen van persoonsgegevens, verzending naar de advertentieplatforms en zelfherstel bij fouten.',
      activeLiveChannels: 'Actieve kanalen',
      inactiveTitle: 'Activeer uw server-side tracking',
      inactiveBody: 'Verbind ten minste één advertentieplatform. Eén kanaal is genoeg om live te gaan: Trackful stelt alles achter de schermen automatisch in (conversieactie, pixel, attributie).',
      connect: 'Verbinden',
      connected: 'Verbonden',
      needsAction: 'Voltooien',
      connectFailed: 'Verbinding mislukt. Probeer het opnieuw.',
      pixelLive: 'Pixel actief, events vastgelegd',
      pixelWaiting: 'Wachten op de eerste pixel-events',
      clickTrackerTitle: 'Activeer kliktracking in uw thema',
      clickTrackerActive: 'Actief',
      clickTrackerCta: 'Nu activeren',
      clickTrackerBody: "De kliktracker legt advertentie-ID's vast (gclid, fbclid...) en plaatst servercookies van 1 jaar: de voorwaarde voor nauwkeurige attributie. Shopify kan dit niet automatisch inschakelen: één klik op Activeren, daarna Opslaan in de thema-editor.",
      clickTrackerHint: 'Open na het opslaan één keer uw winkel: deze stap wordt automatisch groen.',
      sendTestEvent: 'Testconversie versturen',
      testEventOk: 'Testconversie geaccepteerd door Google ✓',
      testEventFail: 'De test is mislukt: open de Google Ads-pagina voor details.',
      consentNotice: 'Compliance: zonder cookiebanner (Consent Mode v2) kunnen EU-conversies geen klantgegevens naar Meta en GA4 sturen. Stel uw CMP in.',
      advanced: 'Geavanceerde opties',
      funnelTitle: 'Volledige-funnel doorsturen',
      funnelBody: 'Stuurt het volledige kooptraject (bekeken producten, winkelwagen-toevoegingen, checkouts) als extra optimalisatiesignalen naar uw advertentieplatforms - niet alleen aankopen. Aankopen worden altijd doorgestuurd.',
      funnelOn: 'Aan',
      funnelOff: 'Uit',
      funnelChannelsTitle: 'Wat elk platform ontvangt',
      funnelChannelsBody: 'Aankopen worden altijd verstuurd. De rest van de journey (productweergaven, toevoegingen aan winkelwagen, checkouts) alleen wanneer wij kunnen garanderen dat die niet dubbel wordt geteld.',
      funnelSends: 'Volledige journey',
      funnelPurchasesOnly: 'Alleen aankopen',
      funnelWhyOwn: 'Onze eigen conversieacties: niets van u kan dubbel worden geteld.',
      funnelWhyStamped: 'Uw tag ({sources}) draagt nu onze event-id, dus het platform telt één event in plaats van twee.',
      funnelWhyEmpty: 'De property ontving niets voordat wij verbonden.',
      funnelWhyDeclared: 'U gaf aan dat Trackful hier uw enige bron is.',
      funnelWhyCollision: 'Een andere bron voedt dit platform al ({sources}). Wij sturen alleen aankopen, zodat uw cijfers niet verdubbelen.',
      funnelAsk: 'Stuurt een andere tag al events vanuit de browser naar dit platform? Native Shopify-verkoopkanalen tellen mee.',
      funnelAskKeep: 'Ja, die houd ik',
      funnelAskOnly: 'Nee, Trackful is mijn enige bron',
      funnelAskWhy: 'Wij kunnen dit zelf niet zien: pixels van native Shopify-kanalen draaien in een geïsoleerde sandbox.',
      pausedTitle: 'Doorsturen gepauzeerd (uw gegevens zijn veilig)',
      pausedCta: 'Upgraden',
      pausedBody: 'U hebt uw planlimiet bereikt. We blijven alles vastleggen, maar het doorsturen naar uw advertentieplatforms is gepauzeerd. Upgrade om te hervatten - we sturen automatisch alles opnieuw wat we hebben vastgelegd.',
      pausedBodyCount: 'Planlimiet bereikt. {count} vastgelegde conversie(s) wachten op verzending. We blijven alles vastleggen; upgrade om het doorsturen te hervatten en we sturen alles automatisch opnieuw.',
    },
    googleAds: {
      title: 'Google Ads',
      subtitle: 'Verbind Google Ads voor Enhanced Conversions tracking',
      connectionStatus: 'Verbindingsstatus',
      account: 'Account',
      conversionAction: 'Conversieactie',
      lastSync: 'Laatste synchronisatie',
      testConnection: 'Verbinding testen',
      connectGoogleAds: 'Google Ads verbinden',
      connectDescription: 'Verbind uw Google Ads-account om conversietracking met Enhanced Conversions te starten.',
      enhancedConversions: 'Enhanced Conversions',
      enhancedConversionsDesc: 'Enhanced Conversions verbetert de trackingnauwkeurigheid door gehashte klantgegevens (e-mail, telefoon, adres) veilig naar Google Ads te sturen.',
      enableEnhancedConversions: 'Enhanced Conversions inschakelen',
      enhancedConversionsHelp: 'Vereist toestemming van klant voor ad_user_data onder AVG/Consent Mode v2',
      ecActionRequiredTitle: 'Actie vereist in Google Ads',
      ecActionRequiredIntro: 'Enhanced Conversions kan niet vanuit deze app worden ingeschakeld. U moet dit instellen in Google Ads (Doelen > Conversies > Instellingen):',
      ecStep1: '1. Schakel "Enhanced conversions for web" in',
      ecStep2: '2. Accepteer de voorwaarden voor klantgegevens',
      ecStep3: '3. Kies "Google Ads API" als methode',
      ecNote: 'Totdat deze stappen zijn voltooid, worden de gehashte klantgegevens die we versturen niet door Google verwerkt.',
      ecReady: 'Enhanced Conversions is klaar: de voorwaarden voor klantgegevens zijn geaccepteerd voor dit Google Ads-account.',
      conversionActionsTitle: 'Conversie-acties',
      conversionActionsDesc: 'Trackful stuurt de aankoop (primaire actie) door en kan ook funnel-stappen als secundaire acties doorsturen.',
      autoCreatedNote: 'Automatisch aangemaakt in uw Google Ads-account toen u het account selecteerde. Te vinden onder Doelen > Conversies.',
      funnelOffHint: 'Volledige-funnel doorsturen staat uit (Dashboard > Geavanceerde opties): deze schakelaars gelden voor secundaire events zodra het is ingeschakeld. Aankopen worden altijd doorgestuurd.',
      disableTitle: 'Doorsturen naar Google Ads uitschakelen?',
      disableConfirmBody: 'De registratie blijft 100% doorlopen: elke conversie wordt nog steeds vastgelegd in Trackful. Alleen het doorsturen naar Google Ads wordt gepauzeerd.',
      disableConfirmSafety: 'Bij het opnieuw inschakelen worden alle tijdens de pauze vastgelegde conversies automatisch opnieuw naar Google Ads gestuurd. U ontvangt een bevestigingsmail.',
      funnelPurchase: 'Aankoop',
      funnelPurchaseDesc: 'De belangrijkste gebeurtenis: de verkoop. Wordt altijd doorgestuurd.',
      primaryBadge: 'Primair',
      createPrimaryAction: 'Aankoopactie aanmaken',
      activateAction: 'Activeren',
      funnelProductView: 'Productweergave',
      funnelProductViewDesc: 'Wanneer een bezoeker een productpagina bekijkt.',
      funnelAddToCart: 'Toevoegen aan winkelwagen',
      funnelAddToCartDesc: 'Wanneer een product aan de winkelwagen wordt toegevoegd. Sterk intentiesignaal.',
      funnelBeginCheckout: 'Afrekenen gestart',
      funnelBeginCheckoutDesc: 'Wanneer een bezoeker het afrekenen start.',
      funnelAddPaymentInfo: 'Betaalgegevens toegevoegd',
      funnelAddPaymentInfoDesc: 'Wanneer betaalgegevens worden ingevoerd.',
      setupInstructions: 'Installatie-instructies',
      step1: '1. Klik op "Google Ads verbinden" om toegang te autoriseren',
      step2: '2. Selecteer het te gebruiken Google Ads-account',
      step3: '3. Kies of maak een conversieactie aan voor aankopen',
      step4: '4. Schakel Enhanced Conversions in voor betere nauwkeurigheid',
      selectAccountTitle: 'Google Ads-account selecteren',
      availableAccounts: 'Beschikbare accounts',
      noAccounts: 'Geen accounts gevonden. Zorg ervoor dat uw Google-account toegang heeft tot minstens één Google Ads-account.',
      selectAccountPrompt: 'Nog geen account geselecteerd. Kies het Google Ads-account dat u wilt volgen.',
      selectConversionTitle: 'Conversieactie selecteren',
      selectConversionDesc: 'Selecteer een bestaande conversieactie of maak een nieuwe aan voor het bijhouden van aankopen.',
      availableActions: 'Beschikbare conversieacties',
      noActions: 'Geen conversieacties gevonden. Klik op "Nieuw aanmaken" om er een te maken.',
      tagBasedAction: 'Tag-gebaseerde actie: kan geen server-side uploads ontvangen. Kies een Trackful-actie of maak er een aan.',
      changeAction: 'Actie wijzigen',
      conflictTitle: 'Risico op dubbele telling gedetecteerd',
      conflictDesc: 'Deze andere aankoop-conversieacties staan ook als Primair ingesteld in uw Google Ads-account. Zolang meerdere aankoopacties primair zijn, worden uw aankopen dubbel geteld in de kolom Conversies en bij het bieden:',
      conflictFix: 'Maak Trackful de enige primaire actie',
      conflictKeep: 'Zo laten',
      conflictConfirmDesc: 'Onderstaande acties worden op Secundair (observatie) gezet in uw Google Ads-account. Ze blijven gemeten maar tellen niet meer mee in de kolom Conversies of bij het bieden. Altijd omkeerbaar.',
      conflictFixed: 'Klaar. De volgende acties zijn nu alleen ter observatie:',
      conflictUndo: 'Ongedaan maken',
      disconnectTitle: 'Google Ads ontkoppelen',
      disconnectConfirm: 'Weet u zeker dat u Google Ads wilt ontkoppelen? Conversietracking stopt tot u opnieuw verbindt.',
      disconnectRemoveActions: 'Verwijder ook de conversieacties die Trackful in Google Ads heeft aangemaakt',
      disconnectRemoveActionsHelp: "Ze krijgen de status 'Verwijderd': ze registreren niet meer, maar uw historische gegevens blijven in uw rapporten.",
      testSuccess: 'Test geslaagd! De configuratie is geldig.',
      testRequestId: 'Google-verzoek-ID',
      testFailed: 'Test mislukt',
      enableTracking: 'Tracking inschakelen',
      disableTracking: 'Tracking uitschakelen',
      actionFailed: 'Er is een fout opgetreden. Probeer het opnieuw.',
    },
    meta: {
      title: 'Meta CAPI',
      subtitle: 'Configureer Meta Conversions API voor server-side tracking',
      connectionStatus: 'Verbindingsstatus',
      pixelId: 'Pixel-ID',
      testEventCode: 'Test-eventcode',
      lastSync: 'Laatste synchronisatie',
      sendTestEvent: 'Test-event versturen',
      updateConfiguration: 'Configuratie wijzigen',
      configureMeta: 'Meta CAPI configureren',
      connectDescription: 'Verbind uw Meta Pixel om conversietracking via de Conversions API te starten.',
      emq: 'Event Match Quality (EMQ)',
      emqDesc: 'EMQ meet hoe goed uw eventgegevens overeenkomen met Meta-gebruikers. Hogere scores betekenen betere attributie en advertentieprestaties.',
      overallScore: 'Totaalscore',
      recommendations: 'Aanbevelingen',
      emqNotAvailable: 'EMQ-gegevens niet beschikbaar. Verstuur events en vernieuw om scores te zien.',
      setupInstructions: 'Installatie-instructies',
      step1: '1. Ga naar uw Meta Events Manager',
      step2: '2. Zoek uw Pixel-ID (Instellingen > Pixel-ID)',
      step3: '3. Genereer een Toegangstoken (Instellingen > Conversions API > Token genereren)',
      step4: '4. Maak optioneel een Test-eventcode aan voor debugging',
      step5: '5. Voer deze waarden in het configuratieformulier in',
      configureTitle: 'Meta CAPI configureren',
      saveConfiguration: 'Configuratie opslaan',
      pixelIdPlaceholder: '1234567890123456',
      pixelIdHelp: 'Uw Meta Pixel-ID uit de Events Manager',
      accessToken: 'Toegangstoken',
      accessTokenPlaceholder: 'EAAx...',
      accessTokenHelp: 'Conversions API toegangstoken (houd dit geheim)',
      testEventCodeOptional: 'Test-eventcode (Optioneel)',
      testEventCodePlaceholder: 'TEST12345',
      testEventCodeHelp: 'Gebruik dit om events te versturen naar Test Events in de Events Manager',
      disconnectTitle: 'Meta CAPI ontkoppelen',
      disconnectConfirm: 'Weet u zeker dat u Meta CAPI wilt ontkoppelen? Conversietracking stopt tot u opnieuw verbindt.',
      testSuccess: 'Test-event succesvol verzonden! Ontvangen events:',
      testFailed: 'Test mislukt',
      required: 'Pixel-ID en Toegangstoken zijn vereist',
      enableTracking: 'Tracking inschakelen',
      disableTracking: 'Tracking uitschakelen',
      actionFailed: 'Er is een fout opgetreden. Probeer het opnieuw.',
    },
    billing: {
      title: 'Facturering',
      subtitle: 'Beheer uw abonnement en monitor uw gebruik',
      currentPlan: 'Huidig plan',
      usage: 'Gebruik',
      ordersUsed: 'Gevolgde bestellingen',
      ordersRemaining: 'Resterende events',
      unlimited: 'Onbeperkt',
      ofLimit: 'van limiet',
      trial: 'Gratis proefperiode',
      trialActive: 'Proefperiode actief',
      trialEndsIn: 'Proefperiode eindigt over',
      trialDaysRemaining: 'resterend',
      days: 'dagen',
      upgradeNow: 'Nu upgraden',
      upgradePlan: 'Plan upgraden',
      downgradePlan: 'Plan downgraden',
      cancelSubscription: 'Abonnement opzeggen',
      scheduledChange: 'Overstap naar {plan} op {date}. Tot dan behoudt u {current}.',
      alreadyCancelled: 'Opgezegd. Toegang loopt tot het einde van de periode.',
      // Billing cadence. `savePercent` and `billedYearly` are templates:
      // the numbers come from the API so the page can never quote a
      // discount the charge does not match.
      billMonthly: 'Maandelijks',
      billAnnually: 'Jaarlijks',
      savePercent: 'Bespaar {percent}%',
      billedYearly: '{amount} per jaar gefactureerd',
      switchToYearly: 'Overstappen op jaarlijks',
      switchToMonthly: 'Overstappen op maandelijks',
      selectPlan: 'Dit plan selecteren',
      currentPlanBadge: 'Huidig plan',
      month: '/maand',
      ordersPerMonth: 'gevolgde bestellingen/maand',
      subscribe: 'Abonneren',
      havePartnerCode: 'Heeft u een partnercode?',
      partnerCodeApplied: 'Partnercode toegepast',
      partnerCodeAppliedHelp: 'Uw korting wordt toegepast voordat u bevestigt, dus de prijs die Shopify toont bevat deze al.',
      partnerCodePrompt: 'Als een partner u een code heeft gegeven, voer deze dan in voordat u zich abonneert.',
      partnerCodeFirstOnly: 'Een partnercode voert u in vóór uw eerste abonnement. Een lopende korting gaat mee bij een planwijziging, zolang u op dezelfde factuurcyclus blijft.',
      partnerCodePlaceholder: 'Optioneel',
      partnerCodeLabel: 'Partnercode',
      partnerCodeHelp: 'Toegepast op uw eerste maand. Een onbekende code betekent simpelweg geen korting.',
      sectionChannels: 'Advertentiekanalen',
      sectionTracking: 'Tracking & attributie',
      sectionCompliance: 'Compliance & toestemming',
      sectionInsights: 'Gegevens & inzichten',
      sectionSupport: 'Ondersteuning',
      features: 'Functies',
      plans: 'Beschikbare plannen',
      starter: 'Starter',
      growth: 'Growth',
      pro: 'Pro',
      warningNearLimit: 'U nadert uw maandelijkse eventlimiet.',
      warningOverLimit: 'U heeft uw eventlimiet overschreden. Upgrade om tracking voort te zetten.',
      warningTrialEnding: 'Uw proefperiode eindigt binnenkort. Abonneer u om door te gaan.',
      billingHistory: 'Factureringsgeschiedenis',
      noHistory: 'Geen factureringsgeschiedenis',
      recommendation: 'Aanbeveling',
      upgradeToRecommended: 'Upgrade naar {plan}',
      plansLoadError: 'Kon plannen niet laden. Vernieuw de pagina.',
      actionFailed: 'Planwijziging mislukt. Probeer het opnieuw.',
      subscriptionSuccess: 'Abonnement succesvol geactiveerd! Je tracking is nu actief.',
      planNowSuccess: 'Klaar. Je zit nu op het {plan}-plan.',
      scheduledSuccess: 'Bevestigd. Je gaat over naar {plan} aan het einde van de periode die je al hebt betaald.',
      scheduledBadge: 'Gepland',
      cancelConfirmationTrial: 'Je zit nog in je gratis proefperiode, er is niets in rekening gebracht. Opzeggen zet je meteen terug op het Free-plan.',
      activationFailed: 'We konden je abonnement niet bevestigen. Probeer het opnieuw of neem contact op met support.',
      planName: 'Plan',
      priceLabel: 'Prijs',
      upgrade: 'Upgraden',
      downgrade: 'Downgraden',
      historyDate: 'Datum',
      historyType: 'Type',
      historyAmount: 'Bedrag',
      historyStatus: 'Status',
      cancelTitle: 'Abonnement opzeggen',
      confirmCancel: 'Ja, opzeggen',
      keepPlan: 'Mijn plan behouden',
      cancelConfirmation: 'Weet je zeker dat je wilt opzeggen? Je plan blijft actief tot het einde van de huidige factureringsperiode.',
      featureLabels: {
        google_ads: 'Google Ads Enhanced Conversions',
        meta_capi: 'Meta Conversions API',
        identity_resolution: 'Identity Resolution',
        basic_support: 'Basissupport',
        priority_support: 'Prioriteitssupport',
        dedicated_support: 'Toegewijd support',
        advanced_analytics: 'Geavanceerde analyses',
        api_access: 'API-toegang',
        ga4: 'Google Analytics 4 (Measurement Protocol)',
        tiktok: 'TikTok Events API',
        pinterest: 'Pinterest Conversions API',
        snapchat: 'Snapchat Conversions API',
        reconciliation: 'Conversie-afstemming',
        live_debugger: 'Live event-debugger',
        alerts: 'Trackingmeldingen',
        export_csv: 'CSV-export',
        webhooks_out: 'Uitgaande webhooks',
        server_side_tracking: 'Server-side tracking (omzeilt adblockers)',
        one_year_cookies: '1-jaar servercookies (Safari ITP-omzeiling)',
        click_id_capture: 'Click-ID-registratie (gclid, fbclid, ttclid, enz.)',
        full_funnel: 'Volledige-funnel doorsturen (weergaven, winkelwagens, checkouts)',
        dedup: 'Deterministische event-deduplicatie',
        auto_backfill: 'Automatisch opnieuw verzenden na een pauze (quotum, uitgeschakeld kanaal)',
        consent_mode: 'Google Consent Mode v2',
        cmp_autodetect: 'CMP-autodetectie (6 aanbieders)',
        gdpr: 'AVG-conform',
        realtime_dashboard: 'Realtime dashboard',
        emq_monitoring: 'Meta EMQ-monitoring',
        all: 'Alle functies',
      },
    },
    settings: {
      title: 'Instellingen',
      subtitle: 'Configureer uw shopinstellingen',
      shopInfo: 'Shopinformatie',
      shopDomain: 'Domein',
      shopName: 'Naam',
      shopId: 'Trackful-ID',
      createdAt: 'Aangemaakt op',
      consentSettings: 'Toestemmingsinstellingen',
      consentDesc: 'Configureer de standaard toestemmingsinstellingen voor Consent Mode v2. Deze waarden worden gebruikt wanneer de bezoeker nog geen toestemming heeft gegeven.',
      defaultConsent: 'Standaard toestemming',
      adStorage: 'Advertentieopslag',
      adStorageHelp: 'Staat opslag van cookies voor advertenties toe',
      analyticsStorage: 'Analyseopslag',
      analyticsStorageHelp: 'Staat opslag van cookies voor analyses toe',
      adUserData: 'Advertentie-gebruikersgegevens',
      adUserDataHelp: 'Staat het verzenden van gebruikersgegevens voor advertenties toe',
      adPersonalization: 'Advertentiepersonalisatie',
      adPersonalizationHelp: 'Staat personalisatie van advertenties toe',
      granted: 'Toegestaan',
      denied: 'Geweigerd',
      unspecified: 'Niet opgegeven (aanbevolen bij een toestemmingsbanner)',
      saveConsent: 'Toestemming opslaan',
      saveFailed: 'Opslaan mislukt',
      language: 'Taal',
      languageHelp: 'Selecteer de interfacetaal',
      french: 'Frans',
      english: 'Engels',
      german: 'Duits',
      spanish: 'Spaans',
      portugueseBR: 'Portugees (Brazilië)',
      japanese: 'Japans',
      dutch: 'Nederlands',
    },
    backfillHold: {
      titleOne: '{n} eerdere bestelling kan naar uw advertentieplatforms worden gestuurd',
      titleMany: '{n} eerdere bestellingen kunnen naar uw advertentieplatforms worden gestuurd',
      action: 'Naar mijn advertentieplatforms sturen',
      actionPending: 'Bezig met verzenden...',
      imported: 'Bij de installatie van Trackful hebben we uw bestellingen van de afgelopen 90 dagen geïmporteerd. Ze tellen al mee in dit dashboard en in Google Analytics 4.',
      notSent: 'We hebben ze niet naar Google Ads, Meta, TikTok, Pinterest of Snapchat gestuurd. Dat zijn uw advertentieaccounts, en als een andere trackingtool dezelfde bestellingen al heeft doorgegeven, zou opnieuw verzenden uw historiek verdubbelen. Er wordt niets verzonden tot u daarvoor kiest.',
      windows: 'Alleen recente bestellingen worden aanvaard: Meta weigert alles ouder dan 7 dagen, Google Ads ongeveer 90. Oudere bestellingen blijven hoe dan ook in uw dashboard staan.',
      error: 'Er ging iets mis bij het verzenden. Er is niets verloren gegaan, u kunt het opnieuw proberen.',
      success: 'Verzonden. Ze verschijnen de komende minuten op uw advertentieplatforms.',
    },
    competingPixels: {
      criticalTitle: 'Uw aankopen worden waarschijnlijk dubbel geteld op Meta',
      criticalAction: 'Meta-instellingen openen',
      criticalDetected: 'We hebben op uw winkel iets gedetecteerd dat lijkt op {sources}. Het stuurt zijn eigen aankoopgebeurtenissen naar Meta, en Trackful stuurt diezelfde aankopen serverzijdig. Die twee bronnen kunnen niet worden ontdubbeld: het verkoopkanaal kiest zijn eigen event-ID’s en toont ze nergens, dus Meta kan de twee gebeurtenissen niet uit elkaar houden.',
      criticalAdvice: 'Houd één bron over, niet twee. We raden aan Trackful te behouden en de tracking van het verkoopkanaal uit te zetten, want Trackful meldt ook de aankopen die een browserpixel verliest door adblockers, iOS-trackingbeperkingen en gehoste afrekenpagina’s. Wilt u liever het verkoopkanaal houden, schakel Meta dan uit in Trackful.',
      warningTitle: 'Er draait een browserpixel naast Trackful',
      warningDetected: 'We hebben {sources} op uw winkel gedetecteerd. Die pixel en Trackful bereiken hetzelfde advertentieaccount, dus dezelfde aankoop kan dubbel worden geteld.',
      warningDedup: 'Deze bronnen beheert u zelf, dus ontdubbelen kan: stuur het event-ID van Trackful mee met de browsergebeurtenis. Bij een aankoop is ons event-ID het {strong}.',
      warningDedupStrong: 'bestelnummer',
      itemMeta: 'Meta Pixel: stel {code} in op het bestelnummer bij uw Purchase-gebeurtenis in de browser.',
      itemOthers: 'TikTok, Pinterest en Snap: stel {code} in op het bestelnummer.',
      itemGtm: 'Google Tag Manager: geef het bestelnummer door aan de tags die de container afvuurt.',
      selfDeduping: '{sources} mag blijven zoals het is: Google Ads ontdubbelt zelf op het bestelnummer.',
      lastDetected: 'Laatst gedetecteerd op uw winkel: {date}',
    },
  },

} as const;

export type TranslationKeys = (typeof translations)[Language];
